import React, { useState, useRef, useEffect , useCallback} from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Settings,
  Users,
  MessageSquare,
  Heart,
  ThumbsDown,
  Pin,
  Send,
  ArrowLeft,
  Clock,
  AlertCircle,
  Trash2,
  Edit3,
  Upload,
  X,
  ChevronDown,
  TrendingUp,
  Calendar,
  Share2,
  Repeat2,
  Link2,
  MoreVertical,
  Trash,
  Bell,
  Shield,
  LogOut,
} from 'lucide-react';
import { PixelatedTurtle, PixelatedCoral } from '@theme/design-constants';
import CoralFrameSvg from '@/assets/CoralFrame.svg';
import { useAuth } from '../../context/AuthContext';
import { ThemeCustomizerButton } from './ThemeCustomizer/index';
import CommunityThemeCustomizer from './ThemeCustomizer/CommunityThemeCustomizer';
import type { ThemeCustomization } from './ThemeCustomizer/ThemeCustomizerModal';
import { PRESET_THEMES } from '@theme/tokens';
import AnimationLayer from './AnimationLayer';
import { getThemeAnimations } from '@theme/animation-config';
import type { ThemeAnimations } from '@theme/animation-config';
import { useTheme } from '../../context/ThemeContext';
import { useNavigation } from '../../context/NavigationContext';
import { useSocket } from '../../context/SocketContext';
import SoftLoad from './SoftLoad';
import { getCommunityPostUrl, getForumPostUrl, getLegacyPostUrl } from '../../utils/postUrls';
import { useScale, scaleW, scaleH } from '../../hooks/useScale';
import { useDynamicBackground } from '../../hooks/useDynamicBackground';
import { useSwipe } from '../../hooks/useSwipe';
import { getMediaUrl } from '../../hooks/useMediaUpload';
import { Post } from '../../types/post';
import { canModeratePost, getEditorType, getPinnerType, User as PermUser } from '../../utils/permissions';
import { selectPreviewComments } from '../../utils/commentPreview';
import { EditPostModal } from './EditPostModal';
import { getBackground, getCardBackground, calculateNavbarBg } from '../../utils/backgroundHelpers';
import { transformColor, type ColorTransformOptions } from '../../utils/colorTransform';
import { PostBadges } from './PostBadges';
import { CommunityPostModal } from './PostCreation';
import MainNavbar from './MainNavbar';
import { postEventBus } from '../../lib/postEventBus';
import { usePostSync } from '../../hooks/usePostSync';
import { commentEventBus } from '../../lib/commentEventBus';
import { useCommentSync } from '../../hooks/useCommentSync';
import { notificationEventBus } from '../../lib/notificationEventBus';
import { useNotificationSync } from '../../hooks/useNotificationSync';
import { cacheTheme, getCachedTheme, createViewOnlyCache } from '../../utils/themeCache';
import { cacheManager } from '../../utils/cacheManager';
import resolveAvatar from '../../lib/resolveAvatar';
import { SubPanelSwiper } from '../mobile/Navigation';
import { MobileSiteThemeCustomizer } from '../mobile/MobileThemeBottomSheet';

import { fetchWithAuth } from '../../lib/fetchWithAuth';
// =====================================================================
// MODULE-LEVEL THEME INITIALIZATION
// =====================================================================
// This runs IMMEDIATELY when the module loads (before React mounts)
// to prevent gray flash during hard reload (F5)
//
// Strategy: Extract communitySlug from URL, check cache, and set sessionStorage
// BEFORE any React components initialize. This ensures LoadingScreen
// sees the correct theme from the very first render.
// =====================================================================

if (typeof window !== 'undefined' && window.location.pathname.startsWith('/community/')) {
  try {
    // Extract community slug from URL (/community/:slug)
    const pathParts = window.location.pathname.split('/');
    const communitySlugFromUrl = pathParts[2];

    if (communitySlugFromUrl) {
      // Check if we have a cached theme for this community
      // We need to iterate through cache keys to find matching community
      const keys = Object.keys(sessionStorage);
      const communityThemeKey = keys.find(key => key.startsWith('communityTheme_'));

      if (communityThemeKey) {
        const cached = sessionStorage.getItem(communityThemeKey);
        if (cached) {
          const parsed = JSON.parse(cached);

          // Extract communityId from the cache key (format: communityTheme_{communityId})
          const communityId = communityThemeKey.replace('communityTheme_', '');

          // Immediately set currentPageTheme for LoadingScreen
          sessionStorage.setItem('currentPageTheme', JSON.stringify({
            type: 'community',
            communityId: communityId,
            colors: {
              primaryColor: parsed.primaryColor,
              secondaryColor: parsed.secondaryColor || parsed.primaryColor,
              textColor: parsed.textColor || '#ffffff',
              accentColor: parsed.accentColor || parsed.primaryColor
            }
          }));

          console.log('🎨 [CommunityClient] Module-level theme initialized for LoadingScreen');
        }
      }
    }
  } catch (e) {
    console.warn('⚠️ [CommunityClient] Module-level theme init failed:', e);
  }
}

type User = {
  id: string;
  username: string;
  displayName: string;
  avatar?: string;
  profileCustomization?: {
    profileFrameColor?: string;
    profileFrameShape?: 'square' | 'circle';
    profileFrameDesign?: 'coral' | 'waves' | 'bubbles';
    selectedDecoration?: string;
  };
  profileDecoration?: string;
};

type Membership = {
  role: 'member' | 'moderator' | 'admin' | 'owner';
  joinedAt: string;
};

type Community = {
  id: string;
  name: string;
  nameSlug?: string;
  description?: string;
  avatar?: string;
  banner?: string;
  profileDecoration?: string | null;
  headerDesign?: string | null;
  isPublic: boolean;
  customCards?: Array<{
    id: string;
    title: string;
    content: string;
    position: number;
  }>;
  memberCount: number;
  postCount: number;
  threadCount: number;
  chatCount: number;
  members: Array<User & { role: string; joinedAt: string }>;
  currentUserRole?: 'member' | 'moderator' | 'admin' | 'owner';
  communityCustomization?: ThemeCustomization;
};

type CommunityPost = {
  id: string;
  title?: string;
  slug?: string;
  postType?: 'community' | 'forum';
  content: string;
  imageUrl?: string;
  mediaUrl?: string;
  aboveContent?: boolean;
  belowContent?: boolean;
  author: User & { role?: string };
  authorId?: string;
  authorUsername?: string;
  createdAt: string;
  pinned: boolean;
  likes: number;
  dislikes?: number;
  liked?: boolean;
  disliked?: boolean;
  replies: Array<{
    id: string;
    content: string;
    author: User;
    likes: number;
    dislikes?: number;
    liked?: boolean;
    disliked?: boolean;
    createdAt: string;
  }>;
  _count: { replies: number; reposts?: number };
  isRepost?: boolean;
  repostComment?: string;
  originalPost?: Omit<CommunityPost, 'originalPost'>;
  editedByModeratorId?: number;
  editedByModeratorAt?: string;
  moderatorEditReason?: string;
  isPinned?: boolean;
  pinnedBy?: string;
  pinnedByType?: 'admin' | 'mod' | 'community_admin' | 'community_mod';
  pinnedAt?: string;
  editedBy?: string;
  editedByType?: 'user' | 'admin' | 'mod' | 'community_admin' | 'community_mod';
  editedAt?: string;
  communityId?: string;
  communitySlug?: string;
};

type CommunityForumPost = {
  id: string;
  title: string;
  slug?: string;
  content: string;
  imageUrl?: string;
  mediaUrl?: string;
  aboveContent?: boolean;
  belowContent?: boolean;
  author: User;
  createdAt: string;
  updatedAt: string;
  pinned: boolean;
  locked: boolean;
  likes: number;
  dislikes?: number;
  liked?: boolean;
  disliked?: boolean;
  replies: Array<{
    id: string;
    content: string;
    author: User;
    createdAt: string;
    likes: number;
    dislikes?: number;
    liked?: boolean;
    disliked?: boolean;
  }>;
  _count: { replies: number; reposts?: number };
};

type ChatMessage = {
  id: string;
  content: string;
  author: User;
  createdAt: string;
  deletedAt?: string;
  deletedBy?: number;
  deleteReason?: string;
};

type Props = {
  community: Community;
  currentUser: (User & { membership: Membership | null }) | null;
  activeTab: 'public' | 'members' | 'staff' | 'forum' | 'chat';
  tabContent: CommunityPost[] | CommunityForumPost[] | ChatMessage[] | null;
  isAdmin: boolean;
  mobileMode?: boolean;
  onSectionChange?: (index: number) => void;
  mobileScreenWidth?: number;
};

// Use ocean-turtle preset as the default for communities
const defaultCommunityCustomization: ThemeCustomization = {
  ...PRESET_THEMES['ocean-turtle'],
  navbarOpacity: 92,
  tabBarOpacity: 88,
  cardGradientFavorability: 50,
  cardOpacity: 100,
  cardGradientStrength: 100,
  gradientEnabled: true,
  gradientAngle: 135,
  gradientFavorability: 50,
  gradientStrength: 100,
  bubbleColor: PRESET_THEMES['ocean-turtle'].accentColor,
  turtleColor: PRESET_THEMES['ocean-turtle'].accentColor, // Use accent color for better visibility
  highlightColor: PRESET_THEMES['ocean-turtle'].accentColor,
  coralColors: [
    PRESET_THEMES['ocean-turtle'].accentColor,
    PRESET_THEMES['ocean-turtle'].secondaryColor || '#00acc1',
    PRESET_THEMES['ocean-turtle'].cardGradientColor2 || '#00897b',
    PRESET_THEMES['ocean-turtle'].cardGradientColor1 || '#00695c',
    PRESET_THEMES['ocean-turtle'].primaryColor || '#006064'
  ],
  profileFrameColor: PRESET_THEMES['ocean-turtle'].accentColor,
  profileFrameShape: 'square',
  profileNameAlign: 'left',
  profileFrameDesign: 'coral',
  animationsEnabled: true,
  selectedCharacter: 'turtle',
  selectedDecoration: 'coral',
  focusAnimationEnabled: false,
  presetTheme: 'ocean-turtle',
  // Alignment and positioning defaults
  cardPosition: 'center',
  tabAlignment: 'left',
  profilePicturePosition: 'left',
  profilePictureSize: 'medium',
  profileTitleAlign: 'left',
  profileBioAlign: 'left',
  postsAlignment: 'center'
};

export default function CommunityClient({
  community,
  currentUser,
  activeTab,
  tabContent,
  isAdmin,
  mobileMode = false,
  onSectionChange,
  mobileScreenWidth = 390,
}: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const { setCurrentTheme } = useNavigation();
  const { logout, user: authUser, token: authToken, setShowLoginModal } = useAuth();
  const { socket } = useSocket();
  const rawScale = useScale();
  const scale = mobileMode ? { w: 1, h: 1 } : rawScale;
  const backgroundHeight = useDynamicBackground();
  const [showCustomizer, setShowCustomizer] = useState(false);
  const [newContent, setNewContent] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [newVisibility, setNewVisibility] = useState<'public' | 'private'>('public');
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [isPosting, setIsPosting] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isFollowing, setIsFollowing] = useState(!!currentUser?.membership || !!community.currentUserRole);
  const [isMutualFollowers, setIsMutualFollowers] = useState(false);
  const [isJoining, setIsJoining] = useState(false);
  const [localMemberCount, setLocalMemberCount] = useState(community.memberCount);
  const [isContentUpdating, setIsContentUpdating] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Focus animation state
  const [focusedPostId, setFocusedPostId] = useState<string | null>(null);
  const [focusedPostIndex, setFocusedPostIndex] = useState<number>(-1);
  const postRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const currentFocusIndexRef = useRef<number>(-1);
  const isThrottledRef = useRef<boolean>(false);

  // Share/Post button state
  const [isShareAnimating, setIsShareAnimating] = useState(false);
  const [showPostWidget, setShowPostWidget] = useState(false);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  // Sort menu state
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [sortBy, setSortBy] = useState<string>("newest");

  const sortOptions = [
    { value: 'newest', label: 'Newest', icon: Calendar },
    { value: 'oldest', label: 'Oldest', icon: Clock },
    { value: 'popular', label: 'Popular', icon: TrendingUp }
  ];

  // Repost modal state
  const [showRepostModal, setShowRepostModal] = useState(false);
  const [repostPostId, setRepostPostId] = useState<string | null>(null);
  const [repostComment, setRepostComment] = useState('');
  const [isReposting, setIsReposting] = useState(false);

  // Share modal state
  const [showShareModal, setShowShareModal] = useState(false);
  const [sharePostId, setSharePostId] = useState<string | null>(null);

  // Draggable modal state
  const [repostModalPosition, setRepostModalPosition] = useState({ x: 0, y: 0 });
  const [isDraggingRepost, setIsDraggingRepost] = useState(false);
  const [dragStartRepost, setDragStartRepost] = useState({ x: 0, y: 0 });
  const repostModalRef = useRef<HTMLDivElement>(null);

  // Chat message state
  const [chatMessage, setChatMessage] = useState('');
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [showModerateMenu, setShowModerateMenu] = useState<string | null>(null);
  const [editingPost, setEditingPost] = useState<Post | null>(null);
  const [posts, setPosts] = useState<CommunityPost[]>([]);
  const [forumPosts, setForumPosts] = useState<CommunityForumPost[]>([]);

  // Listen for regular post updates from other pages/components
  usePostSync({
    onLike: (data) => {
      setPosts(prev => prev.map(p =>
        p.id === String(data.postId)
          ? { ...p, likes: data.likes, liked: data.liked }
          : p
      ));
      // Also update forum posts if the liked post is a forum post
      setForumPosts(prev => prev.map(p =>
        p.id === String(data.postId)
          ? { ...p, likes: data.likes, liked: data.liked }
          : p
      ));
    },
    onDislike: (data) => {
      setPosts(prev => prev.map(p =>
        p.id === String(data.postId)
          ? { ...p, dislikes: data.dislikes, disliked: data.disliked }
          : p
      ));
      // Also update forum posts if the disliked post is a forum post
      setForumPosts(prev => prev.map(p =>
        p.id === String(data.postId)
          ? { ...p, dislikes: data.dislikes, disliked: data.disliked }
          : p
      ));
    },
    postIds: [...posts.map(p => p.id), ...forumPosts.map(p => p.id)]
  });

  // Listen for comment updates from other pages/components
  useCommentSync({
    onLike: (data) => {
      setPosts(prev => prev.map(p => {
        if (p.replies) {
          return {
            ...p,
            replies: p.replies.map((r: any) => {
              // Handle both string and number IDs
              const replyId = String(r.id);
              const eventId = String(data.commentId);
              return replyId === eventId
                ? { ...r, liked: data.liked, likes: data.likes }
                : r;
            }),
          };
        }
        return p;
      }));
    },
    onDislike: (data) => {
      setPosts(prev => prev.map(p => {
        if (p.replies) {
          return {
            ...p,
            replies: p.replies.map((r: any) => {
              // Handle both string and number IDs
              const replyId = String(r.id);
              const eventId = String(data.commentId);
              return replyId === eventId
                ? { ...r, disliked: data.disliked, dislikes: data.dislikes }
                : r;
            }),
          };
        }
        return p;
      }));
    },
  });

  // Notification synchronization for real-time updates
  useNotificationSync({
    onNew: (notification) => {
      setNotifications(prev => [notification, ...prev]);
      setUnreadCount(prev => prev + 1);
    },
    onRead: (notificationId) => {
      setNotifications(prev => prev.map(n => n.id === notificationId ? {...n, read: true} : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    },
    onReadAll: () => {
      setNotifications(prev => prev.map(n => ({...n, read: true})));
      setUnreadCount(0);
    },
    onDelete: (notificationId) => {
      const notification = notifications.find(n => n.id === notificationId);
      if (notification && !notification.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    },
  });

  // Community visual customization state
  const [profileDecoration, setProfileDecoration] = useState<string | null>(community.profileDecoration || null);
  const [headerDesign, setHeaderDesign] = useState<string | null>(community.headerDesign || null);

  // Moderation modal state
  const [showModerationModal, setShowModerationModal] = useState(false);
  const [modCommunityName, setModCommunityName] = useState(community.name);
  const [modCommunityTitle, setModCommunityTitle] = useState('Earth Servers');
  const [modCommunityBio, setModCommunityBio] = useState(community.description || '');

  const currentSortOption = sortOptions.find(opt => opt.value === sortBy) || sortOptions[0];

  // Editable community info (for customizer)
  const [communityName, setCommunityName] = useState(community.name);
  const [communityTitle, setCommunityTitle] = useState('Earth Servers');
  const [communityDescription, setCommunityDescription] = useState(community.description || '');
  const [communityAvatar, setCommunityAvatar] = useState(community.avatar || null);

  const [customization, setCustomization] = useState<ThemeCustomization>(
    community.communityCustomization
      ? {
          // Merge saved customization with defaults to ensure all new properties are present
          ...defaultCommunityCustomization,
          ...community.communityCustomization,
        }
      : {
          // Use default ocean-turtle theme for uncustomized communities
          // This ensures community theme always overrides site theme
          ...defaultCommunityCustomization,
        }
  );
  const [animations, setAnimations] = useState<ThemeAnimations>(() => {
    // Load animations from community customization based on preset theme
    const saved = community.communityCustomization as any;
    const presetTheme = saved?.presetTheme || 'ocean-turtle';
    return getThemeAnimations(presetTheme);
  });
  const [animationsEnabled, setAnimationsEnabled] = useState(() => {
    const saved = community.communityCustomization as any;
    if (typeof saved?.animationsEnabled === 'boolean') {
      return saved.animationsEnabled;
    }
    return true;
  });
  // Removed isThemeLoaded state - no longer needed, theme updates instantly

  // Notifications
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);

  // Community-specific customization state (similar to profileCustomization in ProfileClient)
  // Load saved data with spread operator - more maintainable than listing each field
  type CommunityCustomizationType = {
    profileFrameColor?: string;
    profileFrameShape?: string;
    profileNameAlign?: string;
    profileTitleAlign: 'left' | 'center' | 'right';
    profileBioAlign: 'left' | 'center' | 'right';
    profileFrameDesign?: string;
    cardPosition: 'left' | 'center' | 'right';
    tabAlignment: 'left' | 'center' | 'right';
    profilePicturePosition: 'left' | 'right';
    profilePictureSize: 'small' | 'medium' | 'large';
    postsAlignment: 'left' | 'center' | 'right';
    [key: string]: any; // Allow additional fields from spread
  };

  const [communityCustomization, setCommunityCustomization] = useState<CommunityCustomizationType>(() => {
    const saved = community.communityCustomization as any;
    return {
      profileFrameColor: customization.profileFrameColor,
      profileFrameShape: customization.profileFrameShape,
      profileNameAlign: customization.profileNameAlign,
      profileTitleAlign: (customization.profileNameAlign || 'left') as 'left' | 'center' | 'right',
      profileBioAlign: (customization.profileNameAlign || 'left') as 'left' | 'center' | 'right',
      profileFrameDesign: customization.profileFrameDesign,
      cardPosition: 'center' as 'left' | 'center' | 'right',
      tabAlignment: 'center' as 'left' | 'center' | 'right',
      profilePicturePosition: 'left' as 'left' | 'right',
      profilePictureSize: 'small' as 'small' | 'medium' | 'large',
      postsAlignment: 'center' as 'left' | 'center' | 'right',
      ...saved, // Spread all saved customization fields
    };
  });

  // Section navigation state - map cardPosition to section ('center' -> 'middle')
  const mapCardPositionToSection = (position: 'left' | 'center' | 'right'): 'left' | 'middle' | 'right' => {
    return position === 'center' ? 'middle' : position;
  };
  const [currentSection, setCurrentSection] = useState<'left' | 'middle' | 'right'>(
    mapCardPositionToSection(communityCustomization.cardPosition)
  );

  // Report section changes to mobile shell
  useEffect(() => {
    if (mobileMode && onSectionChange) {
      const sectionIndex = { left: 0, middle: 1, right: 2 }[currentSection];
      onSectionChange(sectionIndex);
    }
  }, [currentSection, mobileMode, onSectionChange]);

  // Swipe handlers
  const swipeRef = useSwipe({
    onSwipeLeft: () => {
      if (currentSection === 'middle') setCurrentSection('right');
      else if (currentSection === 'left') setCurrentSection('middle');
    },
    onSwipeRight: () => {
      if (currentSection === 'middle') setCurrentSection('left');
      else if (currentSection === 'right') setCurrentSection('middle');
    }
  });

  // Check if user can moderate
  const canModerate = currentUser?.membership &&
    ['moderator', 'admin', 'owner'].includes(currentUser.membership.role);

  // Sync layout/structure when community identity changes (for navigation between communities)
  useEffect(() => {
    setProfileDecoration(community.profileDecoration || null);
    setHeaderDesign(community.headerDesign || null);
    setModCommunityName(community.name);
    setModCommunityBio(community.description || '');
    setCommunityName(community.name);
    setCommunityDescription(community.description || '');
    setCommunityAvatar(community.avatar || null);

    // Reset animations from community customization
    const saved = community.communityCustomization as any;
    const presetTheme = saved?.presetTheme || 'ocean-turtle';
    const initialAnimations = getThemeAnimations(presetTheme);
    setAnimations(initialAnimations);
  }, [community.id, community.name, community.description, community.avatar, community.profileDecoration, community.headerDesign, community.communityCustomization]);

  // Create local theme with transformed colors for inline styles
  // This ensures inline styles like style={{ color: localTheme.textColor }} use transformed colors
  const localTheme = React.useMemo(() => {
    const colorSpace = customization.colorSpace || 'Off';

    // Helper to convert hex to rgba with opacity
    const hexToRgbaWithOpacity = (hex: string, opacity: number) => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${opacity / 100})`;
    };

    // If color space is "Off", return raw colors without transformation
    if (colorSpace === 'Off') {
      // Simple: colors are stored as 7-char hex, opacity stored separately
      const navbarBg = customization.navbarBg
        ? hexToRgbaWithOpacity(customization.navbarBg, customization.navbarOpacity ?? 92)
        : hexToRgbaWithOpacity(customization.primaryColor, customization.navbarOpacity ?? 92);

      const tabBarBg = customization.tabBarBg
        ? hexToRgbaWithOpacity(customization.tabBarBg, customization.tabBarOpacity ?? 88)
        : navbarBg;

      const dropdownColor = customization.dropdownColor || customization.tabBarBg || customization.primaryColor;

      return {
        ...theme,
        textColor: customization.textColor,
        accentColor: customization.accentColor,
        primaryColor: customization.primaryColor,
        secondaryColor: customization.secondaryColor,
        cardBg: customization.cardBg,
        profileFrameColor: customization.profileFrameColor || customization.accentColor,
        cardGradientColor1: customization.cardGradientColor1,
        cardGradientColor2: customization.cardGradientColor2,
        navbarBg,
        tabBarBg,
        dropdownColor,
        highlightColor: customization.highlightColor || customization.accentColor,
        bubbleColor: customization.bubbleColor || customization.accentColor,
        turtleColor: customization.turtleColor || customization.accentColor,
      };
    }

    // Otherwise, apply color transformations
    const transformOptions: ColorTransformOptions = {
      colorSpace,
      temperatureLimit: customization.temperatureLimit ?? 50,
      magentaLimit: customization.magentaLimit ?? 50,
      intensityLimit: customization.intensityLimit ?? 50,
      saturationLimit: customization.backgroundSaturationLimit ?? 50,
      brightnessLimit: customization.backgroundBrightnessLimit ?? 50,
      redLimit: customization.redLimit ?? 50,
      greenLimit: customization.greenLimit ?? 50,
      blueLimit: customization.blueLimit ?? 50,
      contrast: customization.backgroundContrast ?? 50,
      hueGravity: customization.backgroundHueGravity ?? 0, // Default 0 = no gravity
      graySaturation: customization.backgroundGraySaturation ?? 0,
      targetHue: customization.backgroundDefaultHue ?? 184, // Default teal hue
    };

    // Transform ALL color properties from customization
    const transformedTextColor = transformColor(customization.textColor, transformOptions);
    const transformedAccentColor = transformColor(customization.accentColor, transformOptions);
    const transformedPrimaryColor = transformColor(customization.primaryColor, transformOptions);
    const transformedSecondaryColor = transformColor(customization.secondaryColor, transformOptions);
    const transformedCardBg = transformColor(customization.cardBg, transformOptions);
    const transformedProfileFrameColor = customization.profileFrameColor ? transformColor(customization.profileFrameColor, transformOptions) : transformedAccentColor;
    const transformedCardGradientColor1 = customization.cardGradientColor1 ? transformColor(customization.cardGradientColor1, transformOptions) : undefined;
    const transformedCardGradientColor2 = customization.cardGradientColor2 ? transformColor(customization.cardGradientColor2, transformOptions) : undefined;

    // Transform navbar and tab bar colors (stored as 7-char hex), then apply opacity
    const transformedNavbarBgColor = customization.navbarBg
      ? transformColor(customization.navbarBg, transformOptions)
      : transformedPrimaryColor;
    const navbarBg = hexToRgbaWithOpacity(transformedNavbarBgColor, customization.navbarOpacity ?? 92);

    const transformedTabBarBgColor = customization.tabBarBg
      ? transformColor(customization.tabBarBg, transformOptions)
      : transformedPrimaryColor;
    const tabBarBg = customization.tabBarBg
      ? hexToRgbaWithOpacity(transformedTabBarBgColor, customization.tabBarOpacity ?? 88)
      : navbarBg;

    const transformedDropdownColor = customization.dropdownColor ? transformColor(customization.dropdownColor, transformOptions) : transformedTabBarBgColor;
    const dropdownColor = transformedDropdownColor;

    const transformedHighlightColor = customization.highlightColor ? transformColor(customization.highlightColor, transformOptions) : transformedAccentColor;
    const transformedBubbleColor = customization.bubbleColor ? transformColor(customization.bubbleColor, transformOptions) : transformedAccentColor;
    const transformedTurtleColor = customization.turtleColor ? transformColor(customization.turtleColor, transformOptions) : transformedAccentColor;

    const result = {
      ...theme,
      textColor: transformedTextColor,
      accentColor: transformedAccentColor,
      primaryColor: transformedPrimaryColor,
      secondaryColor: transformedSecondaryColor,
      cardBg: transformedCardBg,
      profileFrameColor: transformedProfileFrameColor,
      cardGradientColor1: transformedCardGradientColor1,
      cardGradientColor2: transformedCardGradientColor2,
      navbarBg,
      tabBarBg,
      dropdownColor,
      highlightColor: transformedHighlightColor,
      bubbleColor: transformedBubbleColor,
      turtleColor: transformedTurtleColor,
    };

    return {
      ...theme,
      textColor: transformedTextColor,
      accentColor: transformedAccentColor,
      primaryColor: transformedPrimaryColor,
      secondaryColor: transformedSecondaryColor,
      cardBg: transformedCardBg,
      profileFrameColor: transformedProfileFrameColor,
      cardGradientColor1: transformedCardGradientColor1,
      cardGradientColor2: transformedCardGradientColor2,
      navbarBg,
      tabBarBg,
      dropdownColor,
      highlightColor: transformedHighlightColor,
      bubbleColor: transformedBubbleColor,
      turtleColor: transformedTurtleColor,
    };
  }, [customization, theme]);

  // Cache the transformed community theme to sessionStorage for loading screen
  useEffect(() => {
    if (!community.id) return;

    // Cache the transformed theme for this community
    // CRITICAL: Include ALL theme properties so ThemeContext can use complete theme on page refresh
    cacheTheme({
      textColor: localTheme.textColor,
      accentColor: localTheme.accentColor,
      cardBg: localTheme.cardBg,
      appBg: getBackground(localTheme, customization, theme.appBg),
      navbarBg: localTheme.navbarBg,
      tabBarBg: localTheme.tabBarBg,
      primaryColor: localTheme.primaryColor,
      secondaryColor: localTheme.secondaryColor,
      colorSpace: customization.colorSpace || 'Off',
      // Include card gradient settings
      cardGradientColor1: localTheme.cardGradientColor1,
      cardGradientColor2: localTheme.cardGradientColor2,
      // Include opacity settings
      navbarOpacity: (localTheme as any).navbarOpacity,
      tabBarOpacity: (localTheme as any).tabBarOpacity,
      cardOpacity: (localTheme as any).cardOpacity,
      // Include navbar text color
      navbarTextColor: (localTheme as any).navbarTextColor,
      // CRITICAL: Include ALL background gradient settings for LoadingScreen continuity
      gradientEnabled: customization.gradientEnabled ?? true,
      gradientAngle: customization.gradientAngle ?? 135,
      gradientFavorability: customization.gradientFavorability ?? 50,
      gradientStrength: customization.gradientStrength ?? 100,
    }, 'community', community.id);
  }, [localTheme, customization, community.id, theme.appBg]);

  // Sync content updates separately with soft reload (no layout invalidation)
  useEffect(() => {
    // Show soft loading indicator
    setIsContentUpdating(true);

    // Small delay to make the transition visible
    const timer = setTimeout(() => {
      setIsFollowing(!!currentUser?.membership || !!community.currentUserRole);
      setLocalMemberCount(community.memberCount);

      // Hide loading indicator after update
      setTimeout(() => setIsContentUpdating(false), 150);
    }, 100);

    return () => clearTimeout(timer);
  }, [community.memberCount, community.currentUserRole, currentUser?.membership]);

  // Check responsive
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener('resize', handleResize);

    // Listen for community theme updates from customizer (live preview)
    const handleThemeUpdate = (event: any) => {
      const { customization: newCustomization, animations: newAnimations, animationsEnabled: newAnimationsEnabled, communityId: updatedCommunityId } = event.detail;

      // Only apply if it's for this community
      if (updatedCommunityId && updatedCommunityId !== community.id) {
        return;
      }

      if (newCustomization) {
        setCustomization(newCustomization);

        // Also update communityCustomization with alignment values
        setCommunityCustomization(prev => ({
          ...prev,
          profileTitleAlign: newCustomization.profileTitleAlign || prev.profileTitleAlign,
          profileBioAlign: newCustomization.profileBioAlign || prev.profileBioAlign,
          cardPosition: newCustomization.cardPosition || prev.cardPosition,
          tabAlignment: newCustomization.tabAlignment || prev.tabAlignment,
          profilePicturePosition: newCustomization.profilePicturePosition || prev.profilePicturePosition,
          profilePictureSize: newCustomization.profilePictureSize || prev.profilePictureSize,
          postsAlignment: newCustomization.postsAlignment || prev.postsAlignment,
        }));
      }
      if (newAnimations) {
        setAnimations(newAnimations);
      }
      if (typeof newAnimationsEnabled === 'boolean') {
        setAnimationsEnabled(newAnimationsEnabled);
      }
    };
    window.addEventListener('communityThemeUpdated', handleThemeUpdate);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('communityThemeUpdated', handleThemeUpdate);
    };
  }, [community.id]);

  // Fetch notifications on mount
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await fetchWithAuth('/api/notifications', {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setNotifications(data.notifications || []);
          setUnreadCount(data.unreadCount || 0);
        }
      } catch (error) {
      }
    };

    fetchNotifications();
  }, []);

  // Initialize posts from tabContent based on active tab
  useEffect(() => {
    if (Array.isArray(tabContent)) {
      if (activeTab === 'public' || activeTab === 'members' || activeTab === 'staff') {
        // Regular community posts (public/members/staff tabs)
        setPosts(tabContent as CommunityPost[]);
      } else if (activeTab === 'forum') {
        // Forum posts (forum tab)
        setForumPosts(tabContent as CommunityForumPost[]);
      }
    }
  }, [tabContent, activeTab]);

  // Validate posts alignment when card position changes
  useEffect(() => {
    const { cardPosition, postsAlignment } = communityCustomization;

    // If card is on the right and posts are on the left (conflict), change posts to center
    if (cardPosition === 'right' && postsAlignment === 'left') {
      setCommunityCustomization(prev => ({ ...prev, postsAlignment: 'center' }));
    }

    // If card is on the left and posts are on the right (conflict), change posts to center
    if (cardPosition === 'left' && postsAlignment === 'right') {
      setCommunityCustomization(prev => ({ ...prev, postsAlignment: 'center' }));
    }
  }, [communityCustomization.cardPosition, communityCustomization.postsAlignment]);

  // Sync currentSection when cardPosition changes
  useEffect(() => {
    setCurrentSection(mapCardPositionToSection(communityCustomization.cardPosition));
  }, [communityCustomization.cardPosition]);

  // Load community theme from props with multi-tier caching
  useEffect(() => {
    // STEP 1: Try cache first (hot → warm tiers)
    if (!isAdmin) {
      const cached = cacheManager.get('community', community.id);
      if (cached) {
        
        // Apply cached theme immediately
        setCustomization(prev => ({
          ...prev,
          ...cached.settings,
          // Use cached colors (already transformed if colorSpace !== 'Off')
          primaryColor: cached.colors.primaryColor,
          secondaryColor: cached.colors.secondaryColor,
          textColor: cached.colors.textColor,
          accentColor: cached.colors.accentColor,
          navbarBg: cached.colors.navbarBg,
          tabBarBg: cached.colors.tabBarBg,
          cardBg: cached.colors.cardBg,
          appBg: cached.colors.appBg
        }));

        // Load animations
        const presetTheme = cached.settings?.presetTheme || 'ocean-turtle';
        setAnimations(getThemeAnimations(presetTheme));
        if (cached.settings?.animationsEnabled !== undefined) {
          setAnimationsEnabled(cached.settings.animationsEnabled);
        }

        return; // Done - cache hit!
      }
    }

    // STEP 2: Cache miss or admin - load from community.communityCustomization
    if (community.communityCustomization) {
      const saved = community.communityCustomization as any;

      
      // Load all customization properties
      setCustomization({
        ...defaultCommunityCustomization,
        ...saved,
      });

      // Load animations based on preset theme
      const presetTheme = saved.presetTheme || 'ocean-turtle';
      setAnimations(getThemeAnimations(presetTheme));

      // Load animations enabled state
      if (typeof saved.animationsEnabled === 'boolean') {
        setAnimationsEnabled(saved.animationsEnabled);
      }

      // STEP 3: Cache for future visits (view-only cache for non-admins)
      if (!isAdmin && saved.primaryColor) {
        // Build theme to get computed colors
        const baseColors = {
          primaryColor: saved.primaryColor,
          secondaryColor: saved.secondaryColor,
          textColor: saved.textColor,
          accentColor: saved.accentColor,
          navbarBg: saved.navbarBg,
          tabBarBg: saved.tabBarBg,
          cardBg: saved.cardBg,
          appBg: saved.appBg,
          navbarTextColor: saved.navbarTextColor
        };

        // Apply color space transformation if needed
        const colorSpace = saved.colorSpace || 'Off';
        let computedColors = baseColors;

        if (colorSpace !== 'Off') {
          // Transform colors
          const options: ColorTransformOptions = {
            colorSpace,
            temperatureLimit: saved.temperatureLimit,
            magentaLimit: saved.magentaLimit,
            intensityLimit: saved.intensityLimit,
            redLimit: saved.redLimit,
            greenLimit: saved.greenLimit,
            blueLimit: saved.blueLimit,
            backgroundSaturationLimit: saved.backgroundSaturationLimit,
            backgroundBrightnessLimit: saved.backgroundBrightnessLimit,
            backgroundContrast: saved.backgroundContrast,
            backgroundHueGravity: saved.backgroundHueGravity,
            backgroundGraySaturation: saved.backgroundGraySaturation,
            backgroundDefaultHue: saved.backgroundDefaultHue
          };

          computedColors = {
            primaryColor: transformColor(baseColors.primaryColor, options),
            secondaryColor: transformColor(baseColors.secondaryColor, options),
            textColor: baseColors.textColor?.startsWith('#') ? transformColor(baseColors.textColor, options) : baseColors.textColor,
            accentColor: baseColors.accentColor?.startsWith('#') ? transformColor(baseColors.accentColor, options) : baseColors.accentColor,
            navbarBg: baseColors.navbarBg?.startsWith('#') ? transformColor(baseColors.navbarBg, options) : baseColors.navbarBg,
            tabBarBg: baseColors.tabBarBg?.startsWith('#') ? transformColor(baseColors.tabBarBg, options) : baseColors.tabBarBg,
            cardBg: baseColors.cardBg?.startsWith('#') ? transformColor(baseColors.cardBg, options) : baseColors.cardBg,
            appBg: baseColors.appBg?.startsWith('#') ? transformColor(baseColors.appBg, options) : baseColors.appBg,
            navbarTextColor: baseColors.navbarTextColor
          };
        }

        // Create and cache view-only theme
        const cacheData = {
          colors: computedColors,
          settings: saved,
          colorSpace: saved.colorSpace || 'Off',
          communityId: community.id
        };

        cacheManager.set('community', community.id, cacheData);
              }
    }
  }, [community.id, community.communityCustomization, isAdmin]);

  // Register community theme with NavigationContext for LoadingScreen continuity
  // STEP 0: SYNCHRONOUS registration on mount (before any useEffects run)
  // This prevents ThemeContext from briefly showing site theme during reload
  React.useMemo(() => {
    const cached = getCachedTheme('community', community.id);
    if (cached?.primaryColor) {
      // CRITICAL: Set sessionStorage FIRST before calling setCurrentTheme
      // This ensures LoadingScreen sees the correct theme even during hard reload
      try {
        sessionStorage.setItem('currentPageTheme', JSON.stringify({
          type: 'community',
          communityId: community.id,
          colors: {
            primaryColor: cached.primaryColor,
            secondaryColor: cached.secondaryColor || cached.primaryColor,
            textColor: cached.textColor || '#ffffff',
            accentColor: cached.accentColor || cached.primaryColor
          }
        }));
      } catch (e) {
        console.warn('Failed to set currentPageTheme:', e);
      }

      // Now call setCurrentTheme to update NavigationContext
      setCurrentTheme({
        type: 'community',
        communityId: community.id,
        colors: {
          primaryColor: cached.primaryColor,
          secondaryColor: cached.secondaryColor || cached.primaryColor,
          textColor: cached.textColor || '#ffffff',
          accentColor: cached.accentColor || cached.primaryColor
        }
      });
    }
    return null;
  }, [community.id, setCurrentTheme]);

  // STEP 1: Register again in useEffect when theme loads
  // CRITICAL: Register ONCE on mount only, not on tab switches
  const hasRegisteredTheme = useRef(false);
  useEffect(() => {
    if (localTheme.primaryColor && !hasRegisteredTheme.current) {
      hasRegisteredTheme.current = true;
      setCurrentTheme({
        type: 'community',
        communityId: community.id,
        colors: {
          primaryColor: localTheme.primaryColor,
          secondaryColor: localTheme.secondaryColor || localTheme.primaryColor,
          textColor: localTheme.textColor || '#ffffff',
          accentColor: localTheme.accentColor || localTheme.primaryColor
        }
      });
          }
  }, [localTheme.primaryColor, localTheme.secondaryColor, localTheme.textColor, localTheme.accentColor, community.id, customization.colorSpace, setCurrentTheme]);

  // Helper to convert hex to rgba (moved outside useEffect for reusability)
  const hexToRgba = (hex: string, alpha: number) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha / 100})`;
  };

  // Apply community customization as CSS variables when customization changes
  // SKIP in mobileMode: CommunityClient is just one section in MobileShell, not the page owner.
  // Setting global CSS variables here would override the site theme for feed/other sections.
  useEffect(() => {
    if (mobileMode) return;
    const root = document.documentElement;

    // Helper to blend two colors
    const blendColors = (color1: string, color2: string, ratio: number) => {
      const r1 = parseInt(color1.slice(1, 3), 16);
      const g1 = parseInt(color1.slice(3, 5), 16);
      const b1 = parseInt(color1.slice(5, 7), 16);

      const r2 = parseInt(color2.slice(1, 3), 16);
      const g2 = parseInt(color2.slice(3, 5), 16);
      const b2 = parseInt(color2.slice(5, 7), 16);

      const r = Math.round(r1 * (1 - ratio) + r2 * ratio);
      const g = Math.round(g1 * (1 - ratio) + g2 * ratio);
      const b = Math.round(b1 * (1 - ratio) + b2 * ratio);

      return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
    };

    // Apply background gradient
    if (customization.gradientEnabled) {
      const ratio = (customization.gradientFavorability || 50) / 100;
      const blendedColor1 = blendColors(localTheme.primaryColor, localTheme.secondaryColor, ratio);
      const blendedColor2 = blendColors(localTheme.secondaryColor, localTheme.primaryColor, ratio);
      const strength = (customization.gradientStrength || 100) / 100;
      const finalColor1 = blendColors('#808080', blendedColor1, strength);
      const finalColor2 = blendColors('#808080', blendedColor2, strength);

      root.style.setProperty('--app-bg', `linear-gradient(${customization.gradientAngle || 135}deg, ${finalColor1}, ${finalColor2})`);
    } else {
      root.style.setProperty('--app-bg', localTheme.primaryColor);
    }

    // Apply other CSS variables (using transformed colors from localTheme)
    // IMPORTANT: Variable names must match what's used in CSS!
    // CSS uses: var(--text-color) and var(--color-accent)
    root.style.setProperty('--text-color', localTheme.textColor);
    root.style.setProperty('--color-accent', localTheme.accentColor);
    root.style.setProperty('--navbar-bg', localTheme.navbarBg);
    root.style.setProperty('--tab-bar-bg', localTheme.tabBarBg);

    // Apply card background with gradient if enabled
    if (customization.cardGradientEnabled) {
      const ratio = (customization.cardGradientFavorability || 50) / 100;
      const blendedCard1 = blendColors(customization.cardGradientColor1, customization.cardGradientColor2, ratio);
      const blendedCard2 = blendColors(customization.cardGradientColor2, customization.cardGradientColor1, ratio);
      const strength = (customization.cardGradientStrength || 100) / 100;
      const finalCard1 = blendColors(localTheme.cardBg, blendedCard1, strength);
      const finalCard2 = blendColors(localTheme.cardBg, blendedCard2, strength);

      const gradientBg = `linear-gradient(${customization.cardGradientAngle || 120}deg, ${hexToRgba(finalCard1, customization.cardOpacity || 100)}, ${hexToRgba(finalCard2, customization.cardOpacity || 100)})`;
      root.style.setProperty('--card-bg', gradientBg);
    } else {
      root.style.setProperty('--card-bg', hexToRgba(localTheme.cardBg, customization.cardOpacity || 100));
    }
  }, [customization, localTheme, mobileMode]);

  // Helper to apply opacity to dropdown colors
  const getDropdownButtonBg = () => {
    // Use dropdownColor (hex), or fall back to primaryColor (hex) - not tabBarBg which is already rgba
    const baseColor = localTheme.dropdownColor || localTheme.primaryColor;
    const opacity = customization.dropdownButtonOpacity ?? 100;
    return hexToRgba(baseColor, opacity);
  };

  const getDropdownMenuBg = () => {
    // Use dropdownColor (hex), or fall back to primaryColor (hex) - not tabBarBg which is already rgba
    const baseColor = localTheme.dropdownColor || localTheme.primaryColor;
    const opacity = customization.dropdownOpacity ?? 95;
    return hexToRgba(baseColor, opacity);
  };

  // Note: PixelatedTurtle is imported from @theme/design-constants at the top
  // It already accepts a color prop, so no need to redefine it here

  const formatTimeAgo = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return "now";
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;

    return date.toLocaleDateString();
  };

  const formatDate = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Calculate post style based on distance from focused post (pyramid/octagon effect)
  const getPostStyle = (postIndex: number) => {
    // Get the base width from the posts container
    const baseWidth = isMobile ? window.innerWidth * 0.9 : window.innerWidth * 0.5;

    if (!customization.focusAnimationEnabled || focusedPostIndex === -1) {
      // Focus animation is disabled - maintain regular width
      return {
        width: `${baseWidth}px`,
        maxWidth: `${baseWidth}px`,
        transform: 'scale(1)',
        transformOrigin: 'center center',
        opacity: 1,
        willChange: 'transform, width, opacity',
        transition: 'width 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), max-width 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      };
    }

    const distance = Math.abs(postIndex - focusedPostIndex);

    // Focused post is full base width
    if (distance === 0) {
      return {
        width: `${baseWidth}px`,
        maxWidth: `${baseWidth}px`,
        transform: 'scale(1.05)',
        transformOrigin: 'center center',
        opacity: 1,
        willChange: 'transform, width, opacity',
        transition: 'width 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), max-width 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      };
    }

    // Even size reduction with 12% steps, 72% maximum
    const widthPercent = Math.max(72, 100 - (distance * 12));
    const calculatedWidth = (baseWidth * widthPercent) / 100;

    // Calculate scale - subtle scaling for smooth effect
    const scale = Math.max(0.92, 1 - (distance * 0.02));

    // Calculate opacity reduction
    const opacity = Math.max(0.5, 1 - (distance * 0.12));

    return {
      width: `${calculatedWidth}px`,
      maxWidth: `${calculatedWidth}px`,
      transform: `scale(${scale})`,
      transformOrigin: 'center center',
      opacity,
      willChange: 'transform, width, opacity',
      transition: 'width 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), max-width 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    };
  };

  const handleLogout = useCallback(() => {
    logout();
  }, [logout]);

  const handleCreateContent = useCallback(async () => {
    if (!currentUser || !newContent.trim() || isPosting) return;

    setIsPosting(true);

    try {
      const endpoint = activeTab === 'public'
        ? `/api/communities/${community.id}/posts`
        : activeTab === 'members'
        ? `/api/communities/${community.id}/posts`
        : activeTab === 'staff'
        ? `/api/communities/${community.id}/posts`
        : activeTab === 'forum'
        ? `/api/communities/${community.id}/posts`
        : `/api/communities/${community.id}/chat`;

      const body = activeTab === 'forum'
        ? { title: newTitle.trim() || 'Untitled', content: newContent.trim(), postType: 'forum', visibility: newVisibility }
        : activeTab === 'public' || activeTab === 'members' || activeTab === 'staff'
        ? { content: newContent.trim(), visibility: newVisibility }
        : { content: newContent.trim() };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        const data = await response.json();
        setNewContent('');
        setNewTitle('');
        setNewVisibility('public');
        // Reload to fetch the new post
        window.location.reload();
      } else {
        const error = await response.json();
        alert(error.message || error.error || 'Failed to create content');
      }
    } catch (_error) {
      alert('Network error. Please try again.');
    }

    setIsPosting(false);
  }, []); // TODO: Add dependencies

  const handleLike = useCallback(async (type: 'post' | 'reply', id: string) => {
    if (!currentUser) return;

    if (type === 'post') {
      // Find the post in either posts or forumPosts
      const targetPost = posts.find(p => p.id === id) || forumPosts.find(p => p.id === id);
      if (!targetPost) return;

      const wasLiked = targetPost.liked || false;
      const wasDisliked = targetPost.disliked || false;
      const initialLikes = targetPost.likes;
      const initialDislikes = targetPost.dislikes || 0;
      const initialLiked = wasLiked;
      const newLikes = wasLiked ? initialLikes - 1 : initialLikes + 1;
      const newLiked = !wasLiked;
      // Handle mutual exclusivity: if currently disliked, decrement dislikes when liking
      const newDislikes = wasDisliked ? initialDislikes - 1 : initialDislikes;

      // Optimistic update for both regular posts and forum posts
      setPosts(prev => prev.map(post =>
        post.id === id
          ? { ...post, likes: newLikes, liked: newLiked, dislikes: newDislikes, disliked: false }
          : post
      ));
      setForumPosts(prev => prev.map(post =>
        post.id === id
          ? { ...post, likes: newLikes, liked: newLiked, dislikes: newDislikes, disliked: false }
          : post
      ));

      // Emit event for cross-component sync
      postEventBus.emitLike(id, newLiked, newLikes);

      try {
        await fetchWithAuth(`/api/communities/${community.id}/${type}s/${id}/like`, {
          method: 'POST',
          credentials: 'include'
        });
      } catch (_error) {
        // Revert on error for both states
        setPosts(prev => prev.map(post =>
          post.id === id
            ? { ...post, likes: initialLikes, liked: initialLiked }
            : post
        ));
        setForumPosts(prev => prev.map(post =>
          post.id === id
            ? { ...post, likes: initialLikes, liked: initialLiked }
            : post
        ));
        // Emit revert event
        postEventBus.emitLike(id, initialLiked, initialLikes);
      }
    } else {
      // Handle reply likes without state management (not tracked in this component)
      try {
        await fetchWithAuth(`/api/communities/${community.id}/${type}s/${id}/like`, {
          method: 'POST',
          credentials: 'include'
        });
      } catch (_error) {
      }
    }
  }, [posts, forumPosts, community.id, currentUser]);

  const handleDislike = useCallback(async (type: 'post' | 'reply', id: string) => {
    if (!currentUser) return;

    if (type === 'post') {
      // Find the post in either posts or forumPosts
      const targetPost = posts.find(p => p.id === id) || forumPosts.find(p => p.id === id);
      if (!targetPost) return;

      const wasLiked = targetPost.liked || false;
      const wasDisliked = targetPost.disliked || false;
      const initialLikes = targetPost.likes;
      const initialDislikes = targetPost.dislikes || 0;
      const initialDisliked = wasDisliked;
      const newDislikes = wasDisliked ? initialDislikes - 1 : initialDislikes + 1;
      const newDisliked = !wasDisliked;
      // Handle mutual exclusivity: if currently liked, decrement likes when disliking
      const newLikes = wasLiked ? initialLikes - 1 : initialLikes;

      // Optimistic update for both regular posts and forum posts
      setPosts(prev => prev.map(post =>
        post.id === id
          ? { ...post, dislikes: newDislikes, disliked: newDisliked, likes: newLikes, liked: false }
          : post
      ));
      setForumPosts(prev => prev.map(post =>
        post.id === id
          ? { ...post, dislikes: newDislikes, disliked: newDisliked, likes: newLikes, liked: false }
          : post
      ));

      // Emit event for cross-component sync
      postEventBus.emitDislike(id, newDisliked, newDislikes);

      try {
        await fetchWithAuth(`/api/communities/${community.id}/${type}s/${id}/dislike`, {
          method: 'POST',
          credentials: 'include'
        });
      } catch (_error) {
        // Revert on error for both states
        setPosts(prev => prev.map(post =>
          post.id === id
            ? { ...post, dislikes: initialDislikes, disliked: initialDisliked }
            : post
        ));
        setForumPosts(prev => prev.map(post =>
          post.id === id
            ? { ...post, dislikes: initialDislikes, disliked: initialDisliked }
            : post
        ));
        // Emit revert event
        postEventBus.emitDislike(id, initialDisliked, initialDislikes);
      }
    } else {
      // Handle reply dislikes without state management (not tracked in this component)
      try {
        await fetchWithAuth(`/api/communities/${community.id}/${type}s/${id}/dislike`, {
          method: 'POST',
          credentials: 'include'
        });
      } catch (_error) {
      }
    }
  }, [posts, forumPosts, community.id, currentUser]);

  const likeComment = async (commentId: string, postId: string) => {
    const post = posts.find(p => p.id === postId);
    if (!post || !post.replies) return;

    const comment = post.replies.find(r => r.id === commentId);
    if (!comment) return;

    // Capture initial state
    const initialLiked = comment.liked || false;
    const initialLikes = comment.likes || 0;

    // Optimistic update
    const newLiked = !initialLiked;
    const newLikes = initialLiked ? initialLikes - 1 : initialLikes + 1;

    setPosts(prev => prev.map(p => {
      if (p.id === postId && p.replies) {
        return {
          ...p,
          replies: p.replies.map(r =>
            r.id === commentId
              ? { ...r, liked: newLiked, likes: newLikes }
              : r
          ),
        };
      }
      return p;
    }));

    // Emit event to sync across components
    commentEventBus.emitLike(parseInt(commentId), newLiked, newLikes);

    try {
      const response = await fetchWithAuth(`/api/communities/${community.id}/replys/${commentId}/like`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        // Revert on error
        setPosts(prev => prev.map(p => {
          if (p.id === postId && p.replies) {
            return {
              ...p,
              replies: p.replies.map(r =>
                r.id === commentId
                  ? { ...r, liked: initialLiked, likes: initialLikes }
                  : r
              ),
            };
          }
          return p;
        }));
        // Emit revert event
        commentEventBus.emitLike(parseInt(commentId), initialLiked, initialLikes);
      }
    } catch (error) {
      // Revert on error
      setPosts(prev => prev.map(p => {
        if (p.id === postId && p.replies) {
          return {
            ...p,
            replies: p.replies.map(r =>
              r.id === commentId
                ? { ...r, liked: initialLiked, likes: initialLikes }
                : r
            ),
          };
        }
        return p;
      }));
      // Emit revert event
      commentEventBus.emitLike(parseInt(commentId), initialLiked, initialLikes);
    }
  };

  const dislikeComment = async (commentId: string, postId: string) => {
    const post = posts.find(p => p.id === postId);
    if (!post || !post.replies) return;

    const comment = post.replies.find(r => r.id === commentId);
    if (!comment) return;

    // Capture initial state
    const initialDisliked = comment.disliked || false;
    const initialDislikes = comment.dislikes || 0;

    // Optimistic update
    const newDisliked = !initialDisliked;
    const newDislikes = initialDisliked ? initialDislikes - 1 : initialDislikes + 1;

    setPosts(prev => prev.map(p => {
      if (p.id === postId && p.replies) {
        return {
          ...p,
          replies: p.replies.map(r =>
            r.id === commentId
              ? { ...r, disliked: newDisliked, dislikes: newDislikes }
              : r
          ),
        };
      }
      return p;
    }));

    // Emit event to sync across components
    commentEventBus.emitDislike(parseInt(commentId), newDisliked, newDislikes);

    try {
      const response = await fetchWithAuth(`/api/communities/${community.id}/replys/${commentId}/dislike`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        // Revert on error
        setPosts(prev => prev.map(p => {
          if (p.id === postId && p.replies) {
            return {
              ...p,
              replies: p.replies.map(r =>
                r.id === commentId
                  ? { ...r, disliked: initialDisliked, dislikes: initialDislikes }
                  : r
              ),
            };
          }
          return p;
        }));
        // Emit revert event
        commentEventBus.emitDislike(parseInt(commentId), initialDisliked, initialDislikes);
      }
    } catch (error) {
      // Revert on error
      setPosts(prev => prev.map(p => {
        if (p.id === postId && p.replies) {
          return {
            ...p,
            replies: p.replies.map(r =>
              r.id === commentId
                ? { ...r, disliked: initialDisliked, dislikes: initialDislikes }
                : r
            ),
          };
        }
        return p;
      }));
      // Emit revert event
      commentEventBus.emitDislike(parseInt(commentId), initialDisliked, initialDislikes);
    }
  };

  const handleRepost = useCallback((postId: string) => {
    setRepostPostId(postId);
    setShowRepostModal(true);
  }, []); // TODO: Add dependencies

  const handleSubmitRepost = useCallback(async () => {
    if (!currentUser || !repostPostId) return;

    setIsReposting(true);
    try {
      const response = await fetchWithAuth(`/api/posts/${repostPostId}/repost`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ comment: repostComment || undefined })
      });

      if (response.ok) {
        setShowRepostModal(false);
        setRepostComment('');
        setRepostPostId(null);
        alert('Reposted successfully!');
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to repost');
      }
    } catch (_error) {
      alert('Network error. Please try again.');
    }
    setIsReposting(false);
  }, []); // TODO: Add dependencies

  const handleShare = useCallback((postId: string) => {
    setSharePostId(postId);
    setShowShareModal(true);
  }, []); // TODO: Add dependencies

  const handleCopyLink = useCallback(async () => {
    if (!sharePostId) return;
    const url = `${window.location.origin}/communities/${community.id}/posts/${sharePostId}`;

    try {
      await navigator.clipboard.writeText(url);

      // Increment link share count
      await fetchWithAuth(`/api/communities/${community.id}/posts/${sharePostId}/link-share`, {
        method: 'POST',
        credentials: 'include'
      });

      alert('Link copied to clipboard!');
      setShowShareModal(false);
      setSharePostId(null);
    } catch (err) {
      alert('Failed to copy link');
    }
  }, []); // TODO: Add dependencies

  const handleCreatePost = async (postData: {
    title: string;
    content: string;
    tags: string[];
    mediaUrl: string;
    mediaPosition: 'above' | 'below';
    communityId?: string;
    postType?: 'community' | 'forum';
    visibility?: 'public' | 'members-only';
  }) => {
    setIsPosting(true);
    try {
      const communitySlug = community.nameSlug || community.name.toLowerCase().replace(/\s+/g, '-');
      const response = await fetchWithAuth(`/api/communities/${communitySlug}/posts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: postData.title.trim() || undefined,
          content: postData.content,
          tags: postData.tags,
          postType: postData.postType || 'community',
          mediaUrl: postData.mediaUrl || undefined,
          mediaPosition: postData.mediaPosition,
          visibility: postData.visibility || 'public',
        }),
      });

      if (response.ok) {
        // Trigger refetch
        setRefetchTrigger(prev => prev + 1);
        setShowPostWidget(false);
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to create post');
      }
    } catch (error) {
      alert('Failed to create post');
    } finally {
      setIsPosting(false);
    }
  };

  const handleEditPost = useCallback((post: CommunityPost) => {
    setEditingPost(post as any);
    setShowModerateMenu(null);
  }, []); // TODO: Add dependencies

  const handleSaveEdit = useCallback(async (updatedData: { title?: string; content: string; mediaUrl?: string; removeMedia?: boolean }) => {
    if (!editingPost || !currentUser) return;

    const permUser: PermUser = {
      id: parseInt(currentUser.id),
      username: currentUser.username,
      isAdmin: (currentUser as any).isSiteAdmin || (currentUser as any).isSuperAdmin || false,
      isModerator: (currentUser as any).isSiteModerator || false
    };

    const editorType = getEditorType(editingPost, permUser);
    const endpoint = `/api/moderation/communities/${community.id}/posts/${editingPost.id}`;

    try {
      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...updatedData,
          moderatorEditReason: editorType !== 'user' ? 'Post edited by moderator' : undefined
        })
      });

      if (response.ok) {
        const mappedEditorType = editorType === 'moderator' ? 'mod' : editorType === 'community_moderator' ? 'community_mod' : editorType;
        setPosts(prev => prev.map(p => p.id === editingPost.id ? {
          ...p,
          ...updatedData,
          editedBy: currentUser.id.toString(),
          editedByType: mappedEditorType as 'user' | 'admin' | 'mod' | 'community_admin' | 'community_mod',
          editedAt: new Date().toISOString()
        } : p));
        setEditingPost(null);
      }
    } catch (error) {
      throw error;
    }
  }, []); // TODO: Add dependencies

  const handlePinPost = useCallback(async (post: CommunityPost) => {
    if (!currentUser) return;

    const permUser: PermUser = {
      id: parseInt(currentUser.id),
      username: currentUser.username,
      isAdmin: (currentUser as any).isSiteAdmin || (currentUser as any).isSuperAdmin || false,
      isModerator: (currentUser as any).isSiteModerator || false
    };

    const pinnerType = getPinnerType(post as any, permUser);
    const endpoint = `/api/moderation/communities/${community.id}/posts/${post.id}/pin`;

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        const mappedPinnerType = pinnerType === 'moderator' ? 'mod' : pinnerType === 'community_moderator' ? 'community_mod' : pinnerType;
        setPosts(prev => prev.map(p => p.id === post.id ? {
          ...p,
          isPinned: data.pinned,
          pinnedBy: data.pinned ? currentUser.id.toString() : undefined,
          pinnedByType: data.pinned ? mappedPinnerType as 'admin' | 'mod' | 'community_admin' | 'community_mod' : undefined,
          pinnedAt: data.pinned ? new Date().toISOString() : undefined
        } : p));
        setShowModerateMenu(null);
      }
    } catch (error) {
    }
  }, []); // TODO: Add dependencies

  const handlePinToFeed = useCallback(async (post: CommunityPost) => {
    if (!(currentUser as any)?.isSuperAdmin) return;

    try {
      const response = await fetchWithAuth(`/api/moderation/posts/${post.id}/pin-to-feed`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setPosts(prev => prev.map(p => p.id === post.id ? {
          ...p,
          isPinnedToFeed: data.pinnedToFeed,
          pinnedToFeedAt: data.pinnedToFeed ? new Date().toISOString() : undefined
        } : p));
        setShowModerateMenu(null);
      }
    } catch (error) {
    }
  }, []); // TODO: Add dependencies

  const handleDeletePost = useCallback(async (post: CommunityPost) => {
    if (!confirm('Are you sure you want to delete this post?')) {
      return;
    }

    const endpoint = `/api/moderation/communities/${community.id}/posts/${post.id}`;

    try {
      const response = await fetch(endpoint, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setPosts(prev => prev.filter(p => p.id !== post.id));
        setShowModerateMenu(null);
      }
    } catch (error) {
    }
  }, []); // TODO: Add dependencies

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'FOLLOW':
        return <Users size={20} style={{ color: localTheme.textColor }} />;
      case 'NEW_FRIEND':
        return <Heart size={20} style={{ color: localTheme.textColor }} />;
      case 'REPLY':
      case 'COMMUNITY_REPLY':
        return <MessageSquare size={20} style={{ color: localTheme.textColor }} />;
      case 'PROFILE_POST':
        return <Edit3 size={20} style={{ color: localTheme.textColor }} />;
      case 'LIKE':
        return <Heart size={20} style={{ color: localTheme.textColor }} />;
      default:
        return <Bell size={20} style={{ color: localTheme.textColor }} />;
    }
  };

  const getNotificationMessage = (notification: any) => {
    const actor = notification.actorName || 'Someone';
    switch (notification.type) {
      case 'FOLLOW':
        return `${actor} started following you`;
      case 'NEW_FRIEND':
        return `You and ${actor} are now friends!`;
      case 'REPLY':
        return `${actor} replied to your post`;
      case 'COMMUNITY_REPLY':
        return `${actor} replied to your community post`;
      case 'PROFILE_POST':
        return `${actor} posted on your profile`;
      case 'LIKE':
        return `${actor} liked your post`;
      default:
        return notification.content || 'New notification';
    }
  };

  const formatNotificationTime = (createdAt: string) => {
    const date = new Date(createdAt);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const handleNotificationClick = useCallback((notification: any) => {
    setShowNotifications(false);

    switch (notification.type) {
      case 'FOLLOW':
      case 'NEW_FRIEND':
        // Use actorUsername instead of actorId
        if (notification.actorUsername) {
          navigate(`/u/${notification.actorUsername}`);
        } else if (notification.actorId) {
          navigate(`/user/${notification.actorId}`); // Fallback
        }
        break;
      case 'REPLY':
      case 'COMMUNITY_REPLY':
      case 'LIKE':
      case 'DISLIKE':
      case 'REPOST':
        // Route based on postType and slug
        if (notification.postType === 'forum' && notification.communitySlug && notification.postSlug) {
          navigate(getForumPostUrl(notification.communitySlug, notification.postSlug));
        } else if (notification.postType === 'community' && notification.communitySlug && notification.postSlug) {
          navigate(getCommunityPostUrl(notification.communitySlug, notification.postSlug));
        } else if (notification.postType === 'legacy' && notification.postAuthorUsername && notification.postSlug) {
          navigate(getLegacyPostUrl(notification.postAuthorUsername, notification.postSlug));
        } else {
          navigate('/feed'); // Fallback
        }
        break;
      case 'PROFILE_POST':
        navigate('/user');
        break;
      case 'MESSAGE':
        navigate('/messages');
        break;
    }
  }, []); // TODO: Add dependencies

  const handleDeleteMessage = useCallback(async (messageId: string) => {
    if (!currentUser) return;

    const confirmDelete = window.confirm('Are you sure you want to delete this message?');
    if (!confirmDelete) return;

    try {
      const response = await fetchWithAuth(`/api/moderation/communities/${community.id}/chatroom/messages/${messageId}`,
        {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      if (response.ok) {
        // Message deleted via socket.io event
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to delete message');
      }
    } catch (error) {
      alert('Error deleting message');
    }
  }, []); // TODO: Add dependencies

  const handleSendChatMessage = useCallback(async () => {
    if (!chatMessage.trim() || !currentUser || isSendingMessage) return;

    setIsSendingMessage(true);
    const messageContent = chatMessage;
    setChatMessage(''); // Clear input immediately for better UX

    try {
      const response = await fetchWithAuth(`/api/communities/${community.id}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content: messageContent }),
      });


      if (response.ok) {
        const data = await response.json();
        // Message will appear via socket.io event for all users including sender
        // No need to manually add it here
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to send message');
        // Restore message on error
        setChatMessage(messageContent);
      }
    } catch (error) {
      alert('Error sending message');
      // Restore message on error
      setChatMessage(messageContent);
    } finally {
      setIsSendingMessage(false);
    }
  }, []); // TODO: Add dependencies

  const saveCommunity = async () => {
    try {
      const response = await fetchWithAuth(`/api/communities/${community.id}/theme`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          customization: {
            ...communityCustomization,
            ...customization, // Include all theme customizer settings
            profileDecoration: profileDecoration,
            headerDesign: headerDesign,
          },
          animations: animations,
          animationsEnabled: animationsEnabled,
          name: communityName,
          description: communityDescription,
          avatar: communityAvatar,
        })
      });

      if (response.ok) {
        alert('Community customization saved!');
        // No reload needed - customization is already applied in the UI
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to save customization');
      }
    } catch (error) {
      alert('Error saving customization');
    }
  };


  // Initialize chat messages from tabContent
  useEffect(() => {
    if (activeTab === 'chat' && Array.isArray(tabContent)) {
      setChatMessages(tabContent as ChatMessage[]);
    }
  }, [tabContent, activeTab]);

  // Socket listener for real-time chat messages
  useEffect(() => {

    if (!socket || activeTab !== 'chat') {
      return;
    }

    const roomName = `community:${community.id}:chat`;

    // Function to join room
    const joinRoom = () => {
      socket.emit('join', roomName);
    };

    // Join on mount
    joinRoom();

    // Listen for new chat messages
    const handleChatMessage = (message: ChatMessage) => {
      setChatMessages(prev => {
        // Check for duplicates before adding
        if (prev.some(m => m.id === message.id)) {
          return prev;
        }
        const updated = [...prev, message];
        return updated;
      });

      // Scroll to bottom after message is added
      setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    };

    // Handle reconnection
    const handleReconnect = () => {
      joinRoom();
    };

    // Add test listener to verify socket is working
    const testListener = (data: any) => {
    };
    socket.on('test', testListener);

    // Add catch-all listener to see ALL events
    const catchAllListener = (...args: any[]) => {
    };
    socket.onAny(catchAllListener);

    socket.on('community:chatMessage', handleChatMessage);
    socket.on('reconnect', handleReconnect);

    // Verify join after a short delay
    setTimeout(() => {
    }, 500);

    return () => {
      socket.off('community:chatMessage', handleChatMessage);
      socket.off('reconnect', handleReconnect);
      socket.off('test', testListener);
      socket.offAny(catchAllListener);
      socket.emit('leave', roomName);
    };
  }, [socket, community.id, activeTab]);

  // Scroll to bottom when entering chat tab
  useEffect(() => {
    if (activeTab === 'chat' && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [activeTab]);

  // Focus animation effect - scroll-based post focusing
  useEffect(() => {
    if (!customization.focusAnimationEnabled || (activeTab !== 'public' && activeTab !== 'members' && activeTab !== 'staff' && activeTab !== 'forum')) {
      setFocusedPostId(null);
      setFocusedPostIndex(-1);
      return;
    }

    // Build the posts list from actually rendered DOM elements
    // This way we don't duplicate filtering logic - we just use what's actually on screen
    const postsList: { id: string; index: number }[] = [];
    const renderedPosts = Array.from(postRefs.current.entries())
      .map(([id, el]) => {
        const index = parseInt(el.getAttribute('data-index') || '0');
        return { id, index, element: el };
      })
      .sort((a, b) => a.index - b.index);

    renderedPosts.forEach((post, i) => {
      postsList.push({ id: post.id, index: i });
    });

    if (postsList.length === 0) return;

    const handleWheel = (e: WheelEvent) => {
      const direction = e.deltaY > 0 ? 1 : -1;
      const currentIndex = currentFocusIndexRef.current;

      // Check if we're at the edges
      const atTop = currentIndex === 0 && direction === -1;
      const atBottom = currentIndex === postsList.length - 1 && direction === 1;

      // Allow normal scrolling at edges
      if (atTop || atBottom) {
        return;
      }

      e.preventDefault();

      // Update immediately without throttle
      const newIndex = currentIndex + direction;
      currentFocusIndexRef.current = newIndex;

      const focusedPost = postsList[newIndex];
      if (focusedPost) {
        setFocusedPostId(focusedPost.id);
        setFocusedPostIndex(focusedPost.index);
      }
    };

    // Initialize first post as focused (only if not already set)
    if (currentFocusIndexRef.current === -1 || currentFocusIndexRef.current >= postsList.length) {
      currentFocusIndexRef.current = 0;
    }

    if (postsList[currentFocusIndexRef.current]) {
      setFocusedPostId(postsList[currentFocusIndexRef.current].id);
      setFocusedPostIndex(postsList[currentFocusIndexRef.current].index);
    }

    window.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      window.removeEventListener('wheel', handleWheel);
    };
  }, [customization.focusAnimationEnabled, activeTab, tabContent]);

  const CommunityFrameDesign = () => {
    switch (customization.profileFrameDesign) {
      case 'waves':
        return (
          <div className="absolute -bottom-3 left-0 right-0 flex justify-center z-50">
            <svg width="140" height="20" viewBox="0 0 140 20">
              <path d="M0,10 Q35,0 70,10 T140,10" stroke={localTheme.profileFrameColor} strokeWidth="3" fill="none" />
            </svg>
          </div>
        );
      case 'bubbles':
        return (
          <>
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="absolute rounded-full z-50"
                style={{
                  width: '8px',
                  height: '8px',
                  backgroundColor: localTheme.accentColor,
                  top: `${Math.random() * 100}%`,
                  left: `${Math.random() * 100}%`,
                  animation: `float ${3 + i}s infinite ease-in-out`
                }}
              />
            ))}
          </>
        );
      default:
        return (
          <div className="absolute -left-4 -right-4 z-50" style={{ height: '80%', bottom: '-2.5rem' }}>
            <img
              src={CoralFrameSvg}
              alt="Coral frame decoration"
              className="absolute bottom-0 w-full pointer-events-none"
              style={{
                filter: `hue-rotate(${localTheme.accentColor ? '0deg' : '0deg'})`,
                objectFit: 'cover',
                objectPosition: 'bottom center',
                transform: 'scale(1.2)'
              }}
            />
          </div>
        );
    }
  };

  const handleToggleShowSortMenu = useCallback(() => {
    setShowSortMenu(prev => !prev);
  }, []);
  const handleToggleShowCustomizer = useCallback(() => {
    setShowCustomizer(prev => !prev);
  }, []);
  const handleToggleShowModerationModal = useCallback(() => {
    setShowModerationModal(prev => !prev);
  }, []);
  const handleCloseShowRepostModal = useCallback(() => {
    setShowRepostModal(false);
  }, []);
  const handleCloseShowShareModal = useCallback(() => {
    setShowShareModal(false);
  }, []);
  const handleCloseShowPostWidget = useCallback(() => {
    setShowPostWidget(false);
  }, []);
  const handleCloseShowNotifications = useCallback(() => {
    setShowNotifications(false);
  }, []);
  const handleCloseShowSortMenu = useCallback(() => {
    setShowSortMenu(false);
  }, []);
  const handleCloseShowCustomizer = useCallback(() => {
    setShowCustomizer(false);
  }, []);
  const handleCloseShowModerationModal = useCallback(() => {
    setShowModerationModal(false);
  }, []);
  const handleCloseShowModerateMenu = useCallback(() => {
    setShowModerateMenu(null);
  }, []);
  const handleOpenShowRepostModal = useCallback(() => {
    setShowRepostModal(true);
  }, []);
  const handleOpenShowShareModal = useCallback(() => {
    setShowShareModal(true);
  }, []);
  const handleOpenShowPostWidget = useCallback(() => {
    setShowPostWidget(true);
  }, []);

  // ─── Shared CSS styles ───
  const sharedStyles = (
    <style>{`
      @keyframes float {
        0%, 100% { transform: translateY(0) rotate(0deg); }
        50% { transform: translateY(-20px) rotate(10deg); }
      }
      @keyframes swim {
        0%, 100% { transform: translateX(0) translateY(0) scaleX(1); }
        25% { transform: translateX(30px) translateY(-10px) scaleX(1); }
        50% { transform: translateX(0) translateY(-20px) scaleX(-1); }
        75% { transform: translateX(-30px) translateY(-10px) scaleX(-1); }
      }
      .pixelated {
        image-rendering: pixelated;
        image-rendering: -moz-crisp-edges;
        image-rendering: crisp-edges;
      }
    `}</style>
  );

  // ─── Share/Post button (left column in mobile) ───
  const sharePostButton = (
    <button
      onClick={() => {
        setIsShareAnimating(true);
        setTimeout(() => {
          setShowPostWidget(true);
          setIsShareAnimating(false);
        }, 400);
      }}
      className={mobileMode ? "rounded-lg shadow-lg font-semibold overflow-hidden w-full" : "fixed rounded-lg shadow-lg font-semibold overflow-hidden"}
      style={{
        backgroundColor: localTheme.accentColor,
        color: localTheme.textColor,
        zIndex: mobileMode ? 1 : 1000,
        padding: '0.75rem 2rem',
        fontSize: '1.125rem',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: showPostWidget ? 'scale(1.05)' : 'scale(1)',
        ...(!mobileMode ? (communityCustomization.cardPosition === 'left'
          ? { right: '1.5rem', top: '10rem' }
          : { left: '1.5rem', top: '6rem' }) : {})
      }}
    >
      <span style={{
        display: 'inline-block',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        opacity: isShareAnimating ? 0 : 1,
        transform: isShareAnimating ? 'translateX(-20px)' : 'translateX(0)',
      }}>
        {showPostWidget ? 'Post' : 'Share'}
      </span>
    </button>
  );

  // ─── Community Post Modal ───
  const postCreationModal = (
    <CommunityPostModal
      isOpen={showPostWidget}
      onClose={() => setShowPostWidget(false)}
      onSubmit={handleCreatePost}
      currentUser={currentUser}
      theme={localTheme}
      scale={scale}
      scaleW={scaleW}
      scaleH={scaleH}
      getCardBackground={getCardBackground}
      customization={customization}
      communityName={community?.name}
      showPostTypeSelector={true}
      communities={community ? [{ id: community.id, name: community.name, isMember: !!community.currentUserRole }] : []}
      cardPosition={communityCustomization.cardPosition}
      hideCommunitySelector={true}
    />
  );

  // ─── Modals ───
  const renderModals = () => (
    <>
      {/* Repost Modal */}
      {showRepostModal && (
        <div
          ref={repostModalRef}
          className="fixed z-[9999] max-w-md w-full mx-4"
          style={{
            left: '50%',
            top: '5rem',
            transform: `translate(calc(-50% + ${repostModalPosition.x}px), ${repostModalPosition.y}px)`,
            cursor: isDraggingRepost ? 'grabbing' : 'default'
          }}
          onMouseMove={(e) => {
            if (isDraggingRepost) {
              const deltaX = e.clientX - dragStartRepost.x;
              const deltaY = e.clientY - dragStartRepost.y;
              setRepostModalPosition({ x: repostModalPosition.x + deltaX, y: repostModalPosition.y + deltaY });
              setDragStartRepost({ x: e.clientX, y: e.clientY });
            }
          }}
          onMouseUp={() => setIsDraggingRepost(false)}
          onMouseLeave={() => setIsDraggingRepost(false)}
        >
          <div className="rounded-lg backdrop-blur-sm border shadow-2xl" style={{
            background: localTheme.cardBg.replace(/[\d.]+\)$/, '0.95)'),
            borderColor: localTheme.accentColor + '40'
          }}>
            <div
              className="flex items-center justify-between mb-4 p-6 pb-2 cursor-grab active:cursor-grabbing"
              onMouseDown={(e) => {
                setIsDraggingRepost(true);
                setDragStartRepost({ x: e.clientX, y: e.clientY });
              }}
              style={{
                borderBottom: `1px solid ${localTheme.accentColor}20`,
                userSelect: 'none'
              }}
            >
              <h3 className="text-xl font-bold" style={{ color: localTheme.textColor }}>Repost</h3>
              <button
                onClick={() => {
                  setShowRepostModal(false);
                  setRepostModalPosition({ x: 0, y: 0 });
                }}
                className="transition-colors hover:opacity-70"
                style={{ color: localTheme.textColor }}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <X size={24} />
              </button>
            </div>
            <div className="px-6 pb-6">
            <p className="mb-4" style={{ color: localTheme.textColor + 'cc' }}>Add an optional comment to your repost:</p>
            <textarea
              value={repostComment}
              onChange={(e) => setRepostComment(e.target.value)}
              placeholder="What do you think about this?"
              className="w-full p-3 rounded-lg outline-none resize-none transition-all"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: localTheme.textColor,
                border: `1px solid ${localTheme.accentColor}40`
              }}
              rows={4}
              maxLength={280}
            />
            <div className="text-right text-sm mb-4" style={{ color: localTheme.textColor + '99' }}>{repostComment.length}/280</div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowRepostModal(false);
                  setRepostComment('');
                }}
                className="flex-1 py-2 rounded-lg transition-all"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  color: localTheme.textColor,
                  border: `1px solid ${localTheme.accentColor}40`
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitRepost}
                disabled={isReposting}
                className="flex-1 py-2 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: localTheme.accentColor,
                  color: localTheme.textColor
                }}
              >
                {isReposting ? 'Reposting...' : 'Repost'}
              </button>
            </div>
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-[100] max-w-md w-full mx-4">
          <div className="rounded-lg p-6 backdrop-blur-sm border shadow-2xl" style={{
            background: localTheme.cardBg.replace(/[\d.]+\)$/, '0.95)'),
            borderColor: localTheme.accentColor + '40'
          }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold" style={{ color: localTheme.textColor }}>Share Post</h3>
              <button onClick={handleCloseShowShareModal} className="transition-colors hover:opacity-70" style={{ color: localTheme.textColor }}>
                <X size={24} />
              </button>
            </div>
            <p className="mb-4" style={{ color: localTheme.textColor + 'cc' }}>Share this post:</p>
            <button
              onClick={handleCopyLink}
              className="w-full py-3 rounded-lg transition-all flex items-center justify-center gap-2"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: localTheme.textColor,
                border: `1px solid ${localTheme.accentColor}40`
              }}
            >
              <Link2 size={18} />
              Copy Link
            </button>
          </div>
        </div>
      )}

      {/* Edit Post Modal */}
      {editingPost && (
        <EditPostModal
          post={editingPost}
          isOpen={!!editingPost}
          onClose={() => setEditingPost(null)}
          onSave={handleSaveEdit}
        />
      )}

      {/* Moderation Modal */}
      {showModerationModal && currentUser && ((currentUser as any).isSiteModerator || (currentUser as any).isSiteAdmin || (currentUser as any).isSuperAdmin) && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
          onClick={handleCloseShowModerationModal}
        >
          <div
            className="rounded-lg backdrop-blur-sm border p-6 w-[600px] max-h-[80vh] overflow-y-auto"
            style={{
              background: theme.cardBg?.replace(/[\d.]+\)$/, '0.95)') || 'rgba(255, 255, 255, 0.95)',
              borderColor: theme.accentColor + '40'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Shield size={24} style={{ color: theme.accentColor }} />
                <h2 className="text-2xl font-bold" style={{ color: theme.textColor }}>
                  Moderate Community
                </h2>
              </div>
              <button
                onClick={handleCloseShowModerationModal}
                className="p-2 hover:bg-white/10 rounded transition-colors"
                style={{ color: theme.textColor + '99' }}
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              {/* Moderator Features */}
              <div className="space-y-4 pb-4" style={{ borderBottom: `1px solid ${theme.accentColor}20` }}>
                <p className="text-sm font-semibold" style={{ color: theme.accentColor }}>
                  Moderator Features
                </p>

                {/* Community Name */}
                <div>
                  <label className="block text-sm mb-2" style={{ color: theme.textColor + '99' }}>
                    Community Name
                  </label>
                  <input
                    type="text"
                    value={modCommunityName}
                    onChange={(e) => setModCommunityName(e.target.value)}
                    className="w-full px-4 py-2 rounded-lg outline-none"
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      color: theme.textColor,
                      border: `1px solid ${theme.accentColor}40`
                    }}
                  />
                </div>

                {/* Community Title */}
                <div>
                  <label className="block text-sm mb-2" style={{ color: theme.textColor + '99' }}>
                    Community Title
                  </label>
                  <input
                    type="text"
                    value={modCommunityTitle}
                    onChange={(e) => setModCommunityTitle(e.target.value)}
                    className="w-full px-4 py-2 rounded-lg outline-none"
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      color: theme.textColor,
                      border: `1px solid ${theme.accentColor}40`
                    }}
                  />
                </div>

                {/* Bio/Description */}
                <div>
                  <label className="block text-sm mb-2" style={{ color: theme.textColor + '99' }}>
                    Bio
                  </label>
                  <textarea
                    value={modCommunityBio}
                    onChange={(e) => setModCommunityBio(e.target.value)}
                    className="w-full px-4 py-2 rounded-lg outline-none resize-none"
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      color: theme.textColor,
                      border: `1px solid ${theme.accentColor}40`,
                      minHeight: '100px'
                    }}
                  />
                </div>
              </div>

              {/* Admin Features */}
              {(currentUser as any).isAdmin && (
                <div className="space-y-4 pt-4">
                  <p className="text-sm font-semibold" style={{ color: '#ef4444' }}>
                    Admin Features
                  </p>

                  <p className="text-xs" style={{ color: theme.textColor + '80' }}>
                    Additional admin-only features can be added here.
                  </p>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowModerationModal(false);
                  // Reset values
                  setModCommunityName(community.name);
                  setModCommunityTitle('Earth Servers');
                  setModCommunityBio(community.description || '');
                }}
                className="flex-1 px-4 py-3 rounded-lg font-semibold transition-all hover:opacity-80"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  color: theme.textColor,
                  border: `1px solid ${theme.accentColor}40`
                }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  try {
                    const updates: any = {
                      name: modCommunityName,
                      description: modCommunityBio,
                    };

                    const response = await fetchWithAuth(`/api/moderation/communities/${community.id}`, {
                      method: 'PUT',
                      headers: {
                        'Content-Type': 'application/json',
                      },
                      credentials: 'include',
                      body: JSON.stringify(updates),
                    });

                    if (!response.ok) {
                      const error = await response.json();
                      throw new Error(error.error || 'Failed to update community');
                    }

                    const data = await response.json();
                    alert('Community moderation changes saved successfully');
                    setShowModerationModal(false);
                    window.location.reload();
                  } catch (error) {
                    alert(error instanceof Error ? error.message : 'Failed to save changes');
                  }
                }}
                className="flex-1 px-4 py-3 rounded-lg font-semibold transition-all hover:opacity-90"
                style={{
                  backgroundColor: theme.accentColor,
                  color: theme.textColor
                }}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Theme Customizer Modal - use lightweight mobile version in mobileMode */}
      {isAdmin && (
        mobileMode ? (
          <MobileSiteThemeCustomizer
            isOpen={showCustomizer}
            onClose={() => setShowCustomizer(false)}
          />
        ) : (
          <CommunityThemeCustomizer
            isOpen={showCustomizer}
            onClose={() => setShowCustomizer(false)}
            communityId={community.id}
            isOwnerOrAdmin={isAdmin}
            initialCustomization={customization}
            initialAnimations={animations}
            initialAnimationsEnabled={animationsEnabled}
          />
        )
      )}
    </>
  );

  // ─── Mobile Layout ───
  if (mobileMode) {
    return (
      <div style={{ background: getBackground(localTheme, customization, theme.appBg || '#0891b2'), height: '100%', overflow: 'hidden' }}>
        {sharedStyles}
        <SubPanelSwiper
          activePanel={{ left: 0, middle: 1, right: 2 }[currentSection]}
          onPanelChange={(i: number) => {
            const section = (['left', 'middle', 'right'] as const)[i];
            setCurrentSection(section);
          }}
          screenWidth={mobileScreenWidth}
        >
          {[
            <div key="left" className="p-4 h-full overflow-auto">
              {sharePostButton}
              {postCreationModal}
            </div>,
            <div key="center" className="flex flex-col items-center w-full h-full overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
              <div className="p-2 pt-4">
                {/* Community Info Card */}
                <div className="flex w-full" style={{
                  justifyContent: 'center'
                }}>
                  <div className="rounded-2xl p-4 mb-6 relative" style={{
                    background: getCardBackground(localTheme, customization, theme.cardBg),
                    width: '100%',
                  }}>
                    <div className="flex flex-col gap-4" style={{
                      alignItems: 'flex-start'
                    }}>
                      {/* Community Avatar with Frame */}
                      <div className="relative">
                        <div className="relative">
                          <div
                            className={`w-24 h-24 relative overflow-hidden border-4 pixelated ${
                              customization.profileFrameShape === 'circle' ? 'rounded-full' : 'rounded-xl'
                            }`}
                            style={{ borderColor: localTheme.profileFrameColor }}
                          >
                            {communityAvatar ? (
                              <img
                                src={communityAvatar.startsWith('/api/') ? communityAvatar : getMediaUrl(communityAvatar)}
                                alt={community.name}
                                className="w-full h-full object-cover pixelated"
                              />
                            ) : (
                              <div
                                className="w-full h-full flex items-center justify-center"
                                style={{ backgroundColor: 'rgba(16, 185, 129, 0.3)' }}
                              >
                                <Users size={40} className="text-white" />
                              </div>
                            )}
                          </div>
                          <CommunityFrameDesign />
                        </div>
                        <div className="text-center relative z-50" style={{ marginTop: '2.5rem' }}>
                          <p className="text-sm font-medium" style={{
                            color: localTheme.textColor,
                            opacity: isContentUpdating ? 0.5 : 1,
                            transition: 'opacity 0.15s ease'
                          }}>
                            {localMemberCount.toLocaleString()} members
                          </p>
                        </div>
                      </div>

                      {/* Community Info */}
                      <div className="flex-1">
                        <div className="mb-2 flex items-center gap-3">
                          <h1 className="text-2xl font-bold" style={{ color: localTheme.textColor }}>
                            {communityName}
                          </h1>
                          <button
                            onClick={async () => {
                              if (isJoining) return;
                              if (isFollowing) {
                                setIsJoining(true);
                                try {
                                  const response = await fetchWithAuth(`/api/communities/${community.id}/leave`, {
                                    method: 'POST',
                                    credentials: 'include'
                                  });
                                  if (response.ok) {
                                    setIsFollowing(false);
                                    setLocalMemberCount(prev => Math.max(0, prev - 1));
                                  }
                                } catch (error) {
                                } finally {
                                  setIsJoining(false);
                                }
                              } else {
                                setIsJoining(true);
                                try {
                                  const { joinCommunity } = await import('../../lib/api-client');
                                  await joinCommunity(community.id, {});
                                  setIsFollowing(true);
                                  setLocalMemberCount(prev => prev + 1);
                                } catch (error) {
                                  alert(error instanceof Error ? error.message : 'Failed to join community');
                                } finally {
                                  setIsJoining(false);
                                }
                              }
                            }}
                            disabled={isJoining || isContentUpdating}
                            className="rounded-lg font-semibold transition-all hover:opacity-80 disabled:opacity-50"
                            style={{
                              padding: '0.5rem 1.25rem',
                              fontSize: '0.95rem',
                              minWidth: '6rem',
                              backgroundColor: isFollowing ? 'rgba(255, 255, 255, 0.1)' : localTheme.accentColor,
                              color: localTheme.textColor,
                              border: `1px solid ${isFollowing ? localTheme.accentColor : 'transparent'}`,
                              opacity: isContentUpdating ? 0.5 : 1,
                              transition: 'opacity 0.15s ease'
                            }}
                          >
                            {isJoining ? 'Loading...' : isContentUpdating ? 'Updating...' : isFollowing ? 'Joined' : 'Join'}
                          </button>
                        </div>

                        <p className="text-lg mb-3 opacity-90" style={{ color: localTheme.textColor }}>
                          {communityTitle}
                        </p>

                        {communityDescription && (
                          <div className="space-y-2">
                            {communityDescription.split('\n').filter((line: string) => line.trim()).map((line: string, i: number) => (
                              <p key={i} className="text-base italic opacity-85" style={{ color: localTheme.textColor }}>
                                {line}
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Tab Bar */}
                <div className="w-full mb-6">
                  <div className="rounded-lg py-3 backdrop-blur-sm" style={{
                    background: localTheme.tabBarBg,
                    paddingLeft: '1rem',
                    paddingRight: '1rem',
                  }}>
                    <div className="flex items-center overflow-x-auto gap-3">
                      {[
                        { key: 'public', label: 'Public' },
                        { key: 'members', label: 'Members' },
                        { key: 'staff', label: 'Staff' },
                        { key: 'forum', label: 'Forum' },
                        { key: 'chat', label: 'Chat' }
                      ].map(({ key, label }) => (
                        <button
                          key={key}
                          onClick={() => {
                            const slug = community.nameSlug || community.name.toLowerCase().replace(/\s+/g, '-');
                            navigate(`/community/${slug}?tab=${key}`);
                          }}
                          className="text-sm font-semibold pb-1 transition-all duration-300 border-b-2 whitespace-nowrap"
                          style={{
                            color: activeTab === key ? localTheme.textColor : `${localTheme.textColor}99`,
                            borderColor: activeTab === key ? localTheme.accentColor : 'transparent'
                          }}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Posts content - same as desktop but full width */}
                <div className="w-full flex flex-col items-center" style={{ minHeight: `${sortOptions.length * 48 + 80}px` }}>
                  <div style={{ width: '100%' }}>
                    {/* Posts Tab */}
                    {(activeTab === 'public' || activeTab === 'members' || activeTab === 'staff') && (
                      <>
                        {(() => {
                          const postsData = posts || [];
                          let sortedPosts = [...postsData];
                          switch(sortBy) {
                            case 'newest':
                              sortedPosts = sortedPosts.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
                              break;
                            case 'oldest':
                              sortedPosts = sortedPosts.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
                              break;
                            case 'popular':
                              sortedPosts = sortedPosts.sort((a, b) => (b.likes || 0) - (a.likes || 0));
                              break;
                          }
                          return sortedPosts.map((post, index) => {
                            const isForumPost = post.postType === 'forum';
                            return (
                              <div
                                key={post.id}
                                className="rounded-2xl backdrop-blur-sm border relative mb-4"
                                style={{
                                  background: isForumPost ? 'rgba(255, 255, 255, 0.1)' : getCardBackground(localTheme, customization, theme.cardBg),
                                  borderColor: isForumPost ? 'rgba(16, 185, 129, 0.4)' : 'rgba(255, 255, 255, 0.2)',
                                  padding: '1rem',
                                }}>
                                <PostBadges post={post as any} textColor={localTheme.textColor} accentColor={localTheme.accentColor} />
                                <div className="flex items-start gap-3 mb-3">
                                  <div
                                    className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center flex-shrink-0 cursor-pointer"
                                    onClick={() => navigate(`/${post.author.username}`)}
                                  >
                                    {post.author.avatar ? (
                                      <img src={resolveAvatar(post.author.avatar) || post.author.avatar} alt={post.author.displayName} className="w-full h-full rounded-full object-cover" />
                                    ) : (
                                      <PixelatedTurtle size={20} color={localTheme.turtleColor} />
                                    )}
                                  </div>
                                  <div className="flex-1">
                                    {isForumPost && post.title && (
                                      <Link
                                        to={post.slug && community.nameSlug ? getForumPostUrl(community.nameSlug, post.slug) : `/posts/${post.id}`}
                                        className="flex items-center gap-2 mb-1"
                                      >
                                        <h2 className="text-lg font-bold" style={{ color: localTheme.textColor }}>{post.title}</h2>
                                      </Link>
                                    )}
                                    <div className="flex items-center gap-2">
                                      <h3 className="font-semibold text-sm" style={{ color: localTheme.textColor }}>{post.author.displayName}</h3>
                                      <span className="text-xs" style={{ color: `${localTheme.textColor}99` }}>{formatTimeAgo(post.createdAt)}</span>
                                    </div>
                                    {!isForumPost && (
                                      <Link
                                        to={post.slug && community.nameSlug ? getCommunityPostUrl(community.nameSlug, post.slug) : `/posts/${post.id}`}
                                        className="block mt-2"
                                      >
                                        {post.title && <h2 className="text-lg font-bold mb-1" style={{ color: localTheme.textColor }}>{post.title}</h2>}
                                        <p className="leading-relaxed text-sm" style={{ color: localTheme.textColor }}>{post.content}</p>
                                      </Link>
                                    )}
                                  </div>
                                </div>
                                {isForumPost && (
                                  <Link
                                    to={post.slug && community.nameSlug ? getForumPostUrl(community.nameSlug, post.slug) : `/posts/${post.id}`}
                                    className="block"
                                  >
                                    <p className="leading-relaxed text-sm mb-3" style={{ color: localTheme.textColor }}>{post.content}</p>
                                  </Link>
                                )}
                                {post.mediaUrl && (
                                  <div className="my-2 rounded-lg overflow-hidden">
                                    <img src={post.mediaUrl} alt="Post media" className="w-full h-auto" style={{ maxHeight: '300px', objectFit: 'cover' }} />
                                  </div>
                                )}
                                <div className="flex items-center gap-4 pt-3" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                                  <button onClick={() => handleLike('post', post.id)} className="flex items-center gap-1" style={{ color: post.liked ? '#ef4444' : `${localTheme.textColor}99` }}>
                                    <Heart size={16} fill={post.liked ? '#ef4444' : 'none'} />
                                    <span className="text-xs">{post.likes || 0}</span>
                                  </button>
                                  <button onClick={() => handleDislike('post', post.id)} className="flex items-center gap-1" style={{ color: post.disliked ? '#f97316' : `${localTheme.textColor}99` }}>
                                    <ThumbsDown size={16} fill={post.disliked ? '#f97316' : 'none'} />
                                    <span className="text-xs">{post.dislikes || 0}</span>
                                  </button>
                                  <button onClick={() => {
                                    const url = post.postType === 'forum'
                                      ? getForumPostUrl(community?.nameSlug || '', post.slug || '')
                                      : getCommunityPostUrl(community?.nameSlug || '', post.slug || '');
                                    navigate(url);
                                  }} className="flex items-center gap-1" style={{ color: `${localTheme.textColor}99` }}>
                                    <MessageSquare size={16} />
                                    <span className="text-xs">{post._count?.replies || 0}</span>
                                  </button>
                                  <button onClick={() => handleRepost(post.id)} className="flex items-center gap-1" style={{ color: `${localTheme.textColor}99` }}>
                                    <Repeat2 size={16} />
                                    <span className="text-xs">{post._count?.reposts || 0}</span>
                                  </button>
                                  <button onClick={() => handleShare(post.id)} className="flex items-center gap-1" style={{ color: `${localTheme.textColor}99` }}>
                                    <Share2 size={16} />
                                  </button>
                                </div>

                                {/* Comment Previews */}
                                {post.replies && post.replies.length > 0 && (() => {
                                  const mappedReplies = post.replies.map(r => ({
                                    id: r.id,
                                    content: r.content,
                                    author: { id: r.author.id, username: r.author.username, profileName: (r.author as any).profileName, profilePicture: (r.author as any).profilePicture },
                                    likes: r.likes || 0,
                                    dislikes: r.dislikes || 0,
                                    createdAt: r.createdAt,
                                  }));
                                  const previewComments = selectPreviewComments(mappedReplies as any);
                                  return (
                                    <div className="mt-3 pt-3" style={{ borderTop: `1px solid ${localTheme.accentColor}20` }}>
                                      <div className="flex flex-col gap-2.5">
                                        {previewComments.map((reply) => (
                                          <div key={reply.id} className="flex gap-2">
                                            <Link
                                              to={`/user/${reply.author.username}`}
                                              className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                                              style={{ background: `linear-gradient(to bottom right, ${localTheme.accentColor}, ${localTheme.accentColor}dd)` }}
                                            >
                                              {reply.author.profilePicture ? (
                                                <img src={resolveAvatar(reply.author.profilePicture) || reply.author.profilePicture} alt={reply.author.username} className="w-full h-full rounded-full object-cover" />
                                              ) : (
                                                <PixelatedTurtle size={12} color={localTheme.turtleColor} />
                                              )}
                                            </Link>
                                            <div className="flex-1 min-w-0">
                                              <div className="flex items-center gap-1.5 mb-0.5">
                                                <span className="text-xs font-semibold truncate" style={{ color: localTheme.textColor }}>
                                                  {reply.author.profileName || reply.author.username}
                                                </span>
                                                <span className="text-xs shrink-0" style={{ color: localTheme.textColor + '66' }}>
                                                  {new Date(reply.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                                </span>
                                              </div>
                                              <p className="text-xs line-clamp-2" style={{ color: localTheme.textColor + 'cc' }}>{reply.content}</p>
                                              <div className="flex items-center gap-3 mt-1">
                                                <button
                                                  onClick={(e) => { e.stopPropagation(); likeComment(reply.id, post.id); }}
                                                  className="flex items-center gap-1"
                                                  style={{ color: reply.liked ? '#ef4444' : localTheme.textColor + '66' }}
                                                >
                                                  <Heart size={11} fill={reply.liked ? '#ef4444' : 'none'} />
                                                  <span className="text-xs">{reply.likes}</span>
                                                </button>
                                                <button
                                                  onClick={(e) => { e.stopPropagation(); dislikeComment(reply.id, post.id); }}
                                                  className="flex items-center gap-1"
                                                  style={{ color: reply.disliked ? '#f97316' : localTheme.textColor + '66' }}
                                                >
                                                  <ThumbsDown size={11} fill={reply.disliked ? '#f97316' : 'none'} />
                                                  <span className="text-xs">{reply.dislikes}</span>
                                                </button>
                                              </div>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                      {(post._count?.replies || 0) > 3 && (
                                        <button
                                          onClick={() => {
                                            const url = post.postType === 'forum'
                                              ? getForumPostUrl(community?.nameSlug || '', post.slug || '')
                                              : getCommunityPostUrl(community?.nameSlug || '', post.slug || '');
                                            navigate(url);
                                          }}
                                          className="text-xs font-semibold mt-2 block"
                                          style={{ color: localTheme.accentColor }}
                                        >
                                          View all {post._count?.replies || 0} comments
                                        </button>
                                      )}
                                    </div>
                                  );
                                })()}
                              </div>
                            );
                          });
                        })()}
                      </>
                    )}

                    {/* Forum Tab */}
                    {activeTab === 'forum' && (
                      <>
                        {(() => {
                          const forumPostsData = forumPosts || [];
                          let sortedPosts = [...forumPostsData];
                          switch(sortBy) {
                            case 'newest':
                              sortedPosts = sortedPosts.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
                              break;
                            case 'oldest':
                              sortedPosts = sortedPosts.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
                              break;
                            case 'popular':
                              sortedPosts = sortedPosts.sort((a, b) => (b.likes || 0) - (a.likes || 0));
                              break;
                          }
                          return sortedPosts.map((forumPost, index) => (
                            <div
                              key={forumPost.id}
                              className="rounded-2xl backdrop-blur-sm border mb-4"
                              style={{
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                borderColor: 'rgba(255, 255, 255, 0.2)',
                                padding: '1rem',
                              }}
                            >
                              <PostBadges post={forumPost as any} textColor={localTheme.textColor} accentColor={localTheme.accentColor} />
                              <div className="flex items-start gap-3 mb-3">
                                <div
                                  className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center flex-shrink-0 cursor-pointer"
                                  onClick={() => navigate(`/${forumPost.author.username}`)}
                                >
                                  {forumPost.author.avatar ? (
                                    <img src={resolveAvatar(forumPost.author.avatar) || forumPost.author.avatar} alt={forumPost.author.displayName} className="w-full h-full rounded-full object-cover" />
                                  ) : (
                                    <PixelatedTurtle size={20} color={localTheme.turtleColor} />
                                  )}
                                </div>
                                <div className="flex-1">
                                  <Link
                                    to={forumPost.slug && community.nameSlug ? getForumPostUrl(community.nameSlug, forumPost.slug) : `/posts/${forumPost.id}`}
                                    className="flex items-center gap-2 mb-1"
                                  >
                                    <h2 className="text-lg font-bold" style={{ color: localTheme.textColor }}>{forumPost.title}</h2>
                                    {forumPost.pinned && <Pin size={14} style={{ color: localTheme.accentColor }} />}
                                  </Link>
                                  <div className="flex items-center gap-2">
                                    <h3 className="font-semibold text-sm" style={{ color: localTheme.textColor }}>{forumPost.author.displayName}</h3>
                                    <span className="text-xs" style={{ color: `${localTheme.textColor}99` }}>{formatTimeAgo(forumPost.createdAt)}</span>
                                  </div>
                                </div>
                              </div>
                              <Link
                                to={forumPost.slug && community.nameSlug ? getForumPostUrl(community.nameSlug, forumPost.slug) : `/posts/${forumPost.id}`}
                                className="block"
                              >
                                <p className="leading-relaxed text-sm mb-3" style={{ color: localTheme.textColor }}>{forumPost.content}</p>
                              </Link>
                              <div className="flex items-center gap-4 pt-3" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                                <button onClick={() => handleLike('post', forumPost.id)} className="flex items-center gap-1" style={{ color: `${localTheme.textColor}99` }}>
                                  <Heart size={16} fill={forumPost.liked ? '#ef4444' : 'none'} color={forumPost.liked ? '#ef4444' : undefined} />
                                  <span className="text-xs">{forumPost.likes || 0}</span>
                                </button>
                                <button onClick={() => handleDislike('post', forumPost.id)} className="flex items-center gap-1" style={{ color: `${localTheme.textColor}99` }}>
                                  <ThumbsDown size={16} fill={forumPost.disliked ? '#f97316' : 'none'} color={forumPost.disliked ? '#f97316' : undefined} />
                                  <span className="text-xs">{forumPost.dislikes || 0}</span>
                                </button>
                                <button onClick={() => navigate(getForumPostUrl(community?.nameSlug || '', forumPost.slug || ''))} className="flex items-center gap-1" style={{ color: `${localTheme.textColor}99` }}>
                                  <MessageSquare size={16} />
                                  <span className="text-xs">{forumPost._count?.replies || 0}</span>
                                </button>
                                <button onClick={() => handleRepost(forumPost.id)} className="flex items-center gap-1" style={{ color: `${localTheme.textColor}99` }}>
                                  <Repeat2 size={16} />
                                  <span className="text-xs">{forumPost._count?.reposts || 0}</span>
                                </button>
                                <button onClick={() => handleShare(forumPost.id)} className="flex items-center gap-1" style={{ color: `${localTheme.textColor}99` }}>
                                  <Share2 size={16} />
                                </button>
                              </div>

                              {/* Comment Previews */}
                              {forumPost.replies && forumPost.replies.length > 0 && (() => {
                                const mappedReplies = forumPost.replies.map(r => ({
                                  id: r.id,
                                  content: r.content,
                                  author: { id: r.author.id, username: r.author.username, profileName: (r.author as any).profileName, profilePicture: (r.author as any).profilePicture },
                                  likes: r.likes || 0,
                                  dislikes: r.dislikes || 0,
                                  createdAt: r.createdAt,
                                }));
                                const previewComments = selectPreviewComments(mappedReplies as any);
                                return (
                                  <div className="mt-3 pt-3" style={{ borderTop: `1px solid ${localTheme.accentColor}20` }}>
                                    <div className="flex flex-col gap-2.5">
                                      {previewComments.map((reply) => (
                                        <div key={reply.id} className="flex gap-2">
                                          <Link
                                            to={`/user/${reply.author.username}`}
                                            className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                                            style={{ background: `linear-gradient(to bottom right, ${localTheme.accentColor}, ${localTheme.accentColor}dd)` }}
                                          >
                                            {reply.author.profilePicture ? (
                                              <img src={resolveAvatar(reply.author.profilePicture) || reply.author.profilePicture} alt={reply.author.username} className="w-full h-full rounded-full object-cover" />
                                            ) : (
                                              <PixelatedTurtle size={12} color={localTheme.turtleColor} />
                                            )}
                                          </Link>
                                          <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-1.5 mb-0.5">
                                              <span className="text-xs font-semibold truncate" style={{ color: localTheme.textColor }}>
                                                {reply.author.profileName || reply.author.username}
                                              </span>
                                              <span className="text-xs shrink-0" style={{ color: localTheme.textColor + '66' }}>
                                                {new Date(reply.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                              </span>
                                            </div>
                                            <p className="text-xs line-clamp-2" style={{ color: localTheme.textColor + 'cc' }}>{reply.content}</p>
                                            <div className="flex items-center gap-3 mt-1">
                                              <button
                                                onClick={(e) => { e.stopPropagation(); likeComment(reply.id, forumPost.id); }}
                                                className="flex items-center gap-1"
                                                style={{ color: reply.liked ? '#ef4444' : localTheme.textColor + '66' }}
                                              >
                                                <Heart size={11} fill={reply.liked ? '#ef4444' : 'none'} />
                                                <span className="text-xs">{reply.likes}</span>
                                              </button>
                                              <button
                                                onClick={(e) => { e.stopPropagation(); dislikeComment(reply.id, forumPost.id); }}
                                                className="flex items-center gap-1"
                                                style={{ color: reply.disliked ? '#f97316' : localTheme.textColor + '66' }}
                                              >
                                                <ThumbsDown size={11} fill={reply.disliked ? '#f97316' : 'none'} />
                                                <span className="text-xs">{reply.dislikes}</span>
                                              </button>
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                    {(forumPost._count?.replies || 0) > 3 && (
                                      <button
                                        onClick={() => navigate(getForumPostUrl(community?.nameSlug || '', forumPost.slug || ''))}
                                        className="text-xs font-semibold mt-2 block"
                                        style={{ color: localTheme.accentColor }}
                                      >
                                        View all {forumPost._count?.replies || 0} comments
                                      </button>
                                    )}
                                  </div>
                                );
                              })()}
                            </div>
                          ));
                        })()}
                      </>
                    )}

                    {/* Chat Tab */}
                    {activeTab === 'chat' && (
                      <div className="rounded-2xl backdrop-blur-sm border" style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        borderColor: 'rgba(255, 255, 255, 0.2)',
                        width: '100%',
                        height: '500px',
                        marginBottom: '1rem',
                        display: 'flex',
                        flexDirection: 'column'
                      }}>
                        <div className="overflow-y-auto space-y-3" style={{
                          flex: '1 1 auto',
                          display: 'flex',
                          flexDirection: 'column',
                          padding: '0.75rem'
                        }}>
                          {chatMessages?.slice(-100).map((message) => (
                            <div key={message.id} className="flex gap-2">
                              <Link
                                to={`/user/${message.author.username}`}
                                className="relative flex-shrink-0"
                                style={{ width: '2rem', height: '2rem' }}
                              >
                                <div className={`w-full h-full border-2 overflow-hidden pixelated ${(message.author.profileCustomization?.profileFrameShape || 'circle') === 'circle' ? 'rounded-full' : 'rounded-lg'}`}
                                  style={{ borderColor: message.author.profileCustomization?.profileFrameColor || localTheme.accentColor }}
                                >
                                  {message.author.avatar ? (
                                    <img src={resolveAvatar(message.author.avatar) || message.author.avatar} alt={message.author.displayName} className="w-full h-full object-cover pixelated" />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: localTheme.cardBg }}>
                                      <PixelatedTurtle size={16} color={localTheme.turtleColor} />
                                    </div>
                                  )}
                                </div>
                              </Link>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="font-medium text-xs" style={{ color: localTheme.textColor }}>{message.author.displayName}</span>
                                  <span className="text-xs" style={{ color: `${localTheme.textColor}99` }}>{formatTimeAgo(message.createdAt)}</span>
                                </div>
                                {message.deletedAt ? (
                                  <div className="text-xs italic bg-red-900/20 px-2 py-1 rounded border border-red-500/30" style={{ color: `${localTheme.textColor}66` }}>
                                    Message deleted
                                  </div>
                                ) : (
                                  <p className="text-xs" style={{ color: `${localTheme.textColor}e6` }}>{message.content}</p>
                                )}
                              </div>
                            </div>
                          ))}
                          <div ref={messagesEndRef} />
                        </div>
                        <div className="border-t" style={{
                          borderColor: 'rgba(255, 255, 255, 0.2)',
                          backgroundColor: 'rgba(0, 0, 0, 0.2)',
                          padding: '0.75rem',
                          flex: '0 0 auto'
                        }}>
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={chatMessage}
                              onChange={(e) => setChatMessage(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                  e.preventDefault();
                                  handleSendChatMessage();
                                }
                              }}
                              placeholder="Type a message..."
                              disabled={isSendingMessage}
                              className="flex-1 rounded-lg outline-none text-sm"
                              style={{
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                color: localTheme.textColor,
                                border: `1px solid ${localTheme.accentColor}40`,
                                padding: '0.5rem 0.75rem',
                              }}
                            />
                            <button
                              onClick={handleSendChatMessage}
                              disabled={!chatMessage.trim() || isSendingMessage}
                              className="rounded-lg transition-all disabled:opacity-50"
                              style={{
                                backgroundColor: localTheme.accentColor,
                                color: localTheme.textColor,
                                padding: '0.5rem',
                              }}
                            >
                              <Send size={18} />
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>,
            <div key="right" className="p-4 h-full overflow-auto">
              {/* Right column - community info widgets */}
              <div className="rounded-xl p-4 mb-4" style={{
                background: getCardBackground(localTheme, customization, theme.cardBg),
              }}>
                <h3 className="text-sm font-semibold mb-3" style={{ color: localTheme.textColor }}>Community Info</h3>
                <div className="space-y-2 text-xs" style={{ color: `${localTheme.textColor}cc` }}>
                  <p>{localMemberCount.toLocaleString()} members</p>
                  <p>{community.postCount || 0} posts</p>
                  <p>{community.threadCount || 0} threads</p>
                </div>
              </div>
              {/* Sort options */}
              <div className="rounded-xl p-4" style={{
                background: getCardBackground(localTheme, customization, theme.cardBg),
              }}>
                <h3 className="text-sm font-semibold mb-3" style={{ color: localTheme.textColor }}>Sort By</h3>
                <div className="space-y-1">
                  {sortOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setSortBy(option.value)}
                      className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-xs font-medium transition-all"
                      style={{
                        color: localTheme.textColor,
                        backgroundColor: option.value === sortBy ? localTheme.highlightColor : 'transparent',
                      }}
                    >
                      <option.icon size={14} />
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>,
          ]}
        </SubPanelSwiper>
        {renderModals()}
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden" style={{ background: getBackground(localTheme, customization, theme.appBg || '#0891b2'), minHeight: backgroundHeight }}>
      {sharedStyles}

      <AnimationLayer
        animations={animations}
        isMobile={isMobile}
        bubbleColor={localTheme.bubbleColor}
        turtleColor={localTheme.turtleColor}
        enabled={animationsEnabled}
      />

      {/* Top Navigation Bar */}
      <MainNavbar
        key={`navbar-${localTheme.navbarBg}`}
        currentPath="/community"
        onLogout={handleLogout}
        currentUser={currentUser}
        notifications={notifications}
        unreadCount={unreadCount}
        theme={localTheme}
        customization={customization}
        navbarBg={localTheme.navbarBg}
        showNotifications={showNotifications}
        setShowNotifications={setShowNotifications}
        showProfileMenu={showProfileMenu}
        setShowProfileMenu={setShowProfileMenu}
        onNotificationClick={handleNotificationClick}
        getNotificationIcon={getNotificationIcon}
        getNotificationMessage={getNotificationMessage}
        formatNotificationTime={formatNotificationTime}
        setNotifications={setNotifications}
        setUnreadCount={setUnreadCount}
        onSignIn={() => setShowLoginModal(true)}
      />

      <div ref={swipeRef} className={`relative z-10 w-full overflow-hidden ${isMobile ? 'p-2 pt-6' : 'p-4 pt-8'}`}>
        <div
          className="transition-all duration-300"
          style={{
            opacity: currentSection === mapCardPositionToSection(communityCustomization.cardPosition) ? 1 : 0,
            pointerEvents: currentSection === mapCardPositionToSection(communityCustomization.cardPosition) ? 'auto' : 'none'
          }}
        >
        {/* Community Info Card with Card Position */}
        <div className="flex w-full" style={{
          justifyContent: communityCustomization.cardPosition === 'center' ? 'center' : communityCustomization.cardPosition === 'right' ? 'flex-end' : 'flex-start'
        }}>
        <div className={`rounded-2xl ${isMobile ? 'p-4' : 'p-6'} ${isMobile ? 'mb-6' : 'mb-10'} relative`} style={{
          background: getCardBackground(localTheme, customization, theme.cardBg),
          width: isMobile ? '100%' : '50vw',
          aspectRatio: isMobile ? 'auto' : '88/48'
        }}>
          <div className={`flex ${isMobile ? 'flex-col gap-4' : 'gap-6'}`} style={{
            paddingLeft: isMobile ? '0' : (communityCustomization.profilePicturePosition === 'right' ? '2rem' : '1rem'),
            paddingRight: isMobile ? '0' : (communityCustomization.profilePicturePosition === 'right' ? '1rem' : '0'),
            flexDirection: isMobile ? 'column' : (communityCustomization.profilePicturePosition === 'right' ? 'row-reverse' : 'row'),
            alignItems: isMobile ? 'flex-start' : 'flex-start'
          }}>
            {/* Community Avatar with Frame */}
            <div className="relative" style={{
              paddingTop: isMobile ? '0' : '1rem'
            }}>
              <div className="relative">
                <div
                  className={`${isMobile ? 'w-24 h-24' :
                    communityCustomization.profilePictureSize === 'large' ? 'w-48 h-48' :
                    communityCustomization.profilePictureSize === 'medium' ? 'w-40 h-40' :
                    'w-32 h-32'} relative overflow-hidden border-4 pixelated ${
                    customization.profileFrameShape === 'circle' ? 'rounded-full' : 'rounded-xl'
                  }`}
                  style={{ borderColor: localTheme.profileFrameColor }}
                >
                  {communityAvatar ? (
                    <img
                      src={communityAvatar.startsWith('/api/') ? communityAvatar : getMediaUrl(communityAvatar)}
                      alt={community.name}
                      className="w-full h-full object-cover pixelated"
                    />
                  ) : (
                    <div
                      className="w-full h-full flex items-center justify-center"
                      style={{ backgroundColor: 'rgba(16, 185, 129, 0.3)' }}
                    >
                      <Users size={isMobile ? 40 : 60} className="text-white" />
                    </div>
                  )}
                </div>

                {/* overlay decoration */}
                <CommunityFrameDesign />
              </div>

              {/* Member Count under avatar */}
              <div className="text-center relative z-50" style={{ marginTop: '2.5rem' }}>
                <p className="text-sm font-medium" style={{
                  color: localTheme.textColor,
                  opacity: isContentUpdating ? 0.5 : 1,
                  transition: 'opacity 0.15s ease'
                }}>
                  {localMemberCount.toLocaleString()} members
                </p>
              </div>
            </div>

            {/* Community Info */}
            <div className="flex-1" style={{
              position: 'relative',
              paddingTop: isMobile ? '0' : '1rem',
              minHeight: '10rem'
            }}>
              {/* Community Name */}
              <div style={{
                position: isMobile ? 'relative' : 'absolute',
                top: isMobile ? 0 : '1rem',
                ...(customization.profileNameAlign === 'center' && !isMobile ? {
                  left: communityCustomization.profilePicturePosition === 'left' ? '-8rem' : '-10rem',
                  right: communityCustomization.profilePicturePosition === 'left' ? '-2rem' : '0rem',
                  width: communityCustomization.profilePicturePosition === 'left' ? 'calc(100% + 10rem)' : 'calc(100% + 10rem)'
                } : {
                  left: 0,
                  right: 0,
                  width: '100%'
                }),
                ...(customization.profileNameAlign === 'right' && communityCustomization.profilePicturePosition === 'left' && !isMobile ? {
                  paddingRight: '2rem'
                } : {})
              }}>
              <div className="mb-2 flex items-center gap-3" style={{
                justifyContent: customization.profileNameAlign === 'center' ? 'center' : customization.profileNameAlign === 'right' ? 'flex-end' : 'flex-start'
              }}>
              <h1
                className={`${isMobile ? 'text-2xl' : 'text-3xl'} font-bold`}
                style={{
                  color: localTheme.textColor
                }}
              >
                {communityName}
              </h1>
              <button
                onClick={async () => {
                  if (isJoining) return;

                  if (isFollowing) {
                    // Leave community
                    setIsJoining(true);
                    try {
                      const response = await fetchWithAuth(`/api/communities/${community.id}/leave`, {
                        method: 'POST',
                        credentials: 'include'
                      });
                      if (response.ok) {
                        setIsFollowing(false);
                        setLocalMemberCount(prev => Math.max(0, prev - 1));
                      }
                    } catch (error) {
                    } finally {
                      setIsJoining(false);
                    }
                  } else {
                    // Join community
                    setIsJoining(true);
                    try {
                      const { joinCommunity } = await import('../../lib/api-client');
                      await joinCommunity(community.id, {});
                      setIsFollowing(true);
                      setLocalMemberCount(prev => prev + 1);
                    } catch (error) {
                      alert(error instanceof Error ? error.message : 'Failed to join community');
                    } finally {
                      setIsJoining(false);
                    }
                  }
                }}
                disabled={isJoining || isContentUpdating}
                className="rounded-lg font-semibold transition-all hover:opacity-80 disabled:opacity-50"
                style={{
                  padding: '0.5rem 1.25rem',
                  fontSize: '0.95rem',
                  minWidth: '6rem',
                  backgroundColor: isFollowing ? 'rgba(255, 255, 255, 0.1)' : localTheme.accentColor,
                  color: localTheme.textColor,
                  border: `1px solid ${isFollowing ? localTheme.accentColor : 'transparent'}`,
                  opacity: isContentUpdating ? 0.5 : 1,
                  transition: 'opacity 0.15s ease'
                }}
              >
                {isJoining ? 'Loading...' : isContentUpdating ? 'Updating...' : isFollowing ? 'Joined' : 'Join'}
              </button>
              </div>
              </div>

              {/* Community Title */}
              <div style={{
                position: isMobile ? 'relative' : 'absolute',
                top: isMobile ? 0 : '3.5rem',
                ...(communityCustomization.profileTitleAlign === 'center' && !isMobile ? {
                  left: communityCustomization.profilePicturePosition === 'left' ? '-8rem' : '-10rem',
                  right: communityCustomization.profilePicturePosition === 'left' ? '-2rem' : '0rem',
                  width: communityCustomization.profilePicturePosition === 'left' ? 'calc(100% + 10rem)' : 'calc(100% + 10rem)'
                } : {
                  left: 0,
                  right: 0,
                  width: '100%'
                }),
                ...(communityCustomization.profileTitleAlign === 'right' && communityCustomization.profilePicturePosition === 'left' && !isMobile ? {
                  paddingRight: '2rem'
                } : {})
              }}>
              <p className="text-lg mb-3 opacity-90" style={{
                color: localTheme.textColor,
                textAlign: communityCustomization.profileTitleAlign
              }}>
                {communityTitle}
              </p>
              </div>

              {/* Description */}
              {communityDescription && (
                <div style={{
                  position: isMobile ? 'relative' : 'absolute',
                  top: isMobile ? 0 : '6.25rem',
                  ...(communityCustomization.profileBioAlign === 'center' && !isMobile ? {
                    left: communityCustomization.profilePicturePosition === 'left' ? '-8rem' : '-10rem',
                    right: communityCustomization.profilePicturePosition === 'left' ? '-2rem' : '0rem',
                    width: communityCustomization.profilePicturePosition === 'left' ? 'calc(100% + 10rem)' : 'calc(100% + 10rem)'
                  } : {
                    left: 0,
                    right: 0,
                    width: '100%'
                  }),
                  ...(communityCustomization.profileBioAlign === 'right' && communityCustomization.profilePicturePosition === 'left' && !isMobile ? {
                    paddingRight: '2rem'
                  } : {})
                }}>
                <div className="space-y-2">
                  {communityDescription.split('\n').filter((line: string) => line.trim()).map((line: string, i: number) => (
                    <p key={i} className="text-base italic opacity-85" style={{
                      color: localTheme.textColor,
                      textAlign: communityCustomization.profileBioAlign
                    }}>
                      {line}
                    </p>
                  ))}
                </div>
                </div>
              )}
            </div>
          </div>
        </div>
        </div>

        {/* Tab Bar - Aligned with Card */}
        <div className="flex w-full" style={{
          justifyContent: communityCustomization.cardPosition === 'center' ? 'center' : communityCustomization.cardPosition === 'right' ? 'flex-end' : 'flex-start'
        }}>
          <div style={{ width: isMobile ? '100%' : '50vw' }}>
        {/* Tab Bar Container with Alignment */}
        <div className="w-full mb-12" style={{
          display: 'flex',
          justifyContent: communityCustomization.tabAlignment === 'center' ? 'center' : communityCustomization.tabAlignment === 'right' ? 'flex-end' : 'flex-start'
        }}>
        {/* Navigation Tabs Card - Matching FeedClient */}
        <div
          key={`tabbar-${customization.colorSpace}-${customization.redLimit}-${customization.greenLimit}-${customization.blueLimit}`}
          className="rounded-lg py-5 backdrop-blur-sm relative"
          style={{
            background: localTheme.tabBarBg,
            zIndex: 1000,
            width: 'fit-content',
            paddingLeft: '2rem',
            paddingRight: '2rem',
            marginBottom: '2rem'
          }}>
          <div className="flex items-center">
            <div className="flex items-center gap-4">
              {[
                { key: 'public', label: 'Public' },
                { key: 'members', label: 'Members Posts' },
                { key: 'staff', label: 'Staff Posts' },
                { key: 'forum', label: 'Forum' },
                { key: 'chat', label: 'Chat' }
              ].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => {
                    // Auto-center on mobile when chat tab is clicked
                    if (isMobile && key === 'chat') {
                      setCurrentSection(mapCardPositionToSection(communityCustomization.cardPosition));
                    }
                    const slug = community.nameSlug || community.name.toLowerCase().replace(/\s+/g, '-');
                    navigate(`/community/${slug}?tab=${key}`);
                  }}
                  className="text-lg font-semibold pb-1 transition-all duration-300 border-b-2"
                  style={{
                    color: activeTab === key ? localTheme.textColor : `${localTheme.textColor}99`,
                    borderColor: activeTab === key ? localTheme.accentColor : 'transparent'
                  }}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Sort Dropdown */}
            <div className="relative" style={{ zIndex: 41, marginLeft: '4rem' }}>
              <button
                onClick={handleToggleShowSortMenu}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all"
                style={{
                  background: getDropdownButtonBg(),
                  border: 'none',
                  color: localTheme.textColor
                }}
              >
                <currentSortOption.icon size={16} />
                <span>{currentSortOption.label}</span>
                <ChevronDown size={16} className={showSortMenu ? 'rotate-180 transition-transform' : 'transition-transform'} />
              </button>

              {showSortMenu && (
                <>
                  <div
                    className="absolute top-full right-0 rounded-lg overflow-hidden shadow-2xl"
                    style={{
                      backgroundColor: getDropdownMenuBg(),
                      borderColor: localTheme.textColor,
                      border: '2px solid',
                      zIndex: 42,
                      marginTop: `${scaleH(8, scale.h)}px`,
                      minWidth: `${scaleW(160, scale.w)}px`
                    }}
                  >
                    {sortOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => {
                          setSortBy(option.value);
                          setShowSortMenu(false);
                        }}
                        className="flex items-center w-full text-left font-medium transition-all"
                        style={{
                          color: localTheme.textColor,
                          backgroundColor: option.value === sortBy ? localTheme.highlightColor : 'transparent',
                          gap: `${scaleW(8, scale.w)}px`,
                          paddingLeft: `${scaleW(16, scale.w)}px`,
                          paddingRight: `${scaleW(16, scale.w)}px`,
                          paddingTop: `${scaleH(12, scale.h)}px`,
                          paddingBottom: `${scaleH(12, scale.h)}px`,
                          fontSize: `${scaleW(14, scale.w)}px`
                        }}
                        onMouseEnter={(e) => {
                          if (option.value !== sortBy) {
                            e.currentTarget.style.backgroundColor = localTheme.highlightColor + '40';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (option.value !== sortBy) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        <option.icon size={Math.round(scaleW(16, scale.w))} />
                        {option.label}
                      </button>
                    ))}
                  </div>
                  <div
                    className="fixed inset-0"
                    style={{ zIndex: 9998 }}
                    onClick={handleCloseShowSortMenu}
                  />
                </>
              )}
            </div>
          </div>
        </div>
        </div>
        </div>
        </div>

        {/* Posts - Aligned independently based on postsAlignment setting */}
        <div className="w-full" style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: customization.focusAnimationEnabled
            ? 'center'
            : (communityCustomization.postsAlignment === 'center' ? 'center' : communityCustomization.postsAlignment === 'right' ? 'flex-end' : 'flex-start'),
          // Keep room below the tab bar for the sort dropdown even when there are no posts
          // (the page root is overflow-hidden and only as tall as its content).
          minHeight: `${sortOptions.length * 56 + 96}px`
        }}>
          <div style={{
            width: customization.focusAnimationEnabled
              ? '100%'  // Full width for focus animation to allow posts to shrink/grow
              : (isMobile ? '100%' : '50vw'),
            display: 'flex',
            flexDirection: 'column',
            alignItems: customization.focusAnimationEnabled
              ? 'center'
              : (communityCustomization.postsAlignment === 'center' ? 'center' : communityCustomization.postsAlignment === 'right' ? 'flex-end' : 'flex-start'),
            maxWidth: isMobile ? '100%' : '60vw',
            paddingLeft: isMobile ? `${scaleW(8, scale.w)}px` : 0,
            paddingRight: isMobile ? `${scaleW(8, scale.w)}px` : 0
          }}>
            {/* Posts Tab */}
            {(activeTab === 'public' || activeTab === 'members' || activeTab === 'staff') && (
              <>
                {(() => {
                  // Use posts from state (initialized from tabContent, updated by event bus)
                  const postsData = posts || [];

                  // Apply sorting (backend provides default, but allow client-side override)
                  let sortedPosts = [...postsData];
                  switch(sortBy) {
                    case 'newest':
                      sortedPosts = sortedPosts.sort((a, b) =>
                        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
                      );
                      break;
                    case 'oldest':
                      sortedPosts = sortedPosts.sort((a, b) =>
                        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
                      );
                      break;
                    case 'popular':
                      sortedPosts = sortedPosts.sort((a, b) => (b.likes || 0) - (a.likes || 0));
                      break;
                    default:
                      break;
                  }

                  return sortedPosts.map((post, index) => {
                  const postStyle = getPostStyle(index);
                  const isForumPost = post.postType === 'forum';
                  return (
                  <div
                    key={post.id}
                    ref={(el) => {
                      if (el) postRefs.current.set(post.id, el);
                      else postRefs.current.delete(post.id);
                    }}
                    data-post-id={post.id}
                    data-index={index}
                    className="rounded-2xl backdrop-blur-sm border relative"
                    style={{
                      background: isForumPost ? 'rgba(255, 255, 255, 0.1)' : getCardBackground(localTheme, customization, theme.cardBg),
                      borderColor: isForumPost ? 'rgba(16, 185, 129, 0.4)' : 'rgba(255, 255, 255, 0.2)',
                      marginBottom: isMobile ? `${scaleH(16, scale.h)}px` : '2rem',
                      padding: isMobile ? `${scaleH(16, scale.h)}px ${scaleW(12, scale.w)}px` : '1.5rem',
                      ...postStyle,
                    }}>
                    {/* Post Badges */}
                    <PostBadges post={post as any} textColor={localTheme.textColor} accentColor={localTheme.accentColor} />

                    {/* Moderate Dropdown */}
                    {currentUser && canModeratePost(post as any, {
                      id: parseInt(currentUser.id),
                      username: currentUser.username,
                      isAdmin: (currentUser as any).isSiteAdmin || (currentUser as any).isSuperAdmin || false,
                      isModerator: (currentUser as any).isSiteModerator || false
                    }) && (
                    <div className="absolute top-3 right-3" style={{ zIndex: 10 }}>
                      <button
                        onClick={() => setShowModerateMenu(showModerateMenu === post.id ? null : post.id)}
                        className="p-1.5 rounded-lg transition-all hover:opacity-80"
                        style={{
                          backgroundColor: localTheme.accentColor + '20',
                          color: localTheme.textColor
                        }}
                      >
                        <MoreVertical size={16} />
                      </button>

                      {showModerateMenu === post.id && (
                        <>
                          <div
                            className="absolute top-full right-0 mt-2 rounded-lg overflow-hidden shadow-2xl min-w-[140px]"
                            style={{
                              backgroundColor: localTheme.tabBarBg || 'rgba(30, 58, 95, 0.95)',
                              borderColor: localTheme.accentColor + '60',
                              border: '1px solid',
                              zIndex: 1001
                            }}
                          >
                            <button
                              onClick={() => handleEditPost(post)}
                              className="flex items-center gap-2 w-full px-4 py-3 text-left text-sm font-medium transition-all"
                              style={{
                                color: localTheme.textColor,
                                backgroundColor: 'transparent'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = localTheme.accentColor + '20';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }}
                            >
                              <Edit3 size={16} />
                              Edit
                            </button>
                            <button
                              onClick={() => handlePinPost(post)}
                              className="flex items-center gap-2 w-full px-4 py-3 text-left text-sm font-medium transition-all"
                              style={{
                                color: localTheme.textColor,
                                backgroundColor: 'transparent'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = localTheme.accentColor + '20';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }}
                            >
                              <Pin size={16} />
                              {post.isPinned ? 'Unpin' : 'Pin'}
                            </button>
                            {(currentUser as any)?.isSuperAdmin && (
                              <button
                                onClick={() => handlePinToFeed(post)}
                                className="flex items-center gap-2 w-full px-4 py-3 text-left text-sm font-medium transition-all"
                                style={{ color: localTheme.textColor, backgroundColor: 'transparent' }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = localTheme.accentColor + '20'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                              >
                                <Pin size={16} />
                                {(post as any).isPinnedToFeed ? 'Unpin from Feed' : 'Pin to Feed'}
                              </button>
                            )}
                            <button
                              onClick={() => handleDeletePost(post)}
                              className="flex items-center gap-2 w-full px-4 py-3 text-left text-sm font-medium transition-all"
                              style={{
                                color: '#ef4444',
                                backgroundColor: 'transparent'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#ef444420';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }}
                            >
                              <Trash size={16} />
                              Delete
                            </button>
                          </div>
                          <div
                            className="fixed inset-0"
                            style={{ zIndex: 90 }}
                            onClick={handleCloseShowModerateMenu}
                          />
                        </>
                      )}
                    </div>
                    )}
                    <div className="flex items-start gap-4 mb-4">
                      <div
                        className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/${post.author.username}`);
                        }}
                        role="button"
                        tabIndex={0}
                      >
                        {post.author.avatar ? (
                          <img src={resolveAvatar(post.author.avatar) || post.author.avatar} alt={post.author.displayName} className="w-full h-full rounded-full object-cover" />
                        ) : (
                          <PixelatedTurtle key={`turtle-${localTheme.turtleColor}`} size={24} color={localTheme.turtleColor} />
                        )}
                      </div>
                      <div className="flex-1">
                        {isForumPost && post.title && (
                          <Link
                            to={
                              post.slug && community.nameSlug
                                ? getForumPostUrl(community.nameSlug, post.slug)
                                : `/posts/${post.id}`
                            }
                            state={{
                              from: 'community',
                              communityId: community.id,
                              themeContext: {
                                type: 'community',
                                communityId: community.id,
                                colors: {
                                  primaryColor: localTheme.primaryColor,
                                  secondaryColor: localTheme.secondaryColor,
                                  textColor: localTheme.textColor,
                                  accentColor: localTheme.accentColor
                                }
                              }
                            }}
                            className="flex items-center gap-2 mb-2 hover:opacity-80 transition-opacity cursor-pointer"
                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                          >
                            <h2 className="text-xl font-bold" style={{ color: localTheme.textColor }}>{post.title}</h2>
                            {post.pinned && <Pin size={16} style={{ color: localTheme.accentColor }} />}
                          </Link>
                        )}
                        <div className="flex flex-col gap-0.5 mb-2">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold" style={{ color: localTheme.textColor }}>{post.author.displayName}</h3>
                            <span className="text-sm" style={{ color: `${localTheme.textColor}99` }}>{formatTimeAgo(post.createdAt)}</span>
                            {!isForumPost && post.pinned && <Pin size={16} style={{ color: localTheme.accentColor }} />}
                          </div>
                          {post.author.username && (
                            <span className="text-xs" style={{ color: `${localTheme.textColor}99` }}>@{post.author.username}</span>
                          )}
                        </div>

                        {!isForumPost && post.editedByModeratorId && (
                          <div className="mb-3 px-3 py-2 rounded border" style={{
                            backgroundColor: '#854d0e20',
                            borderColor: '#ca8a0480'
                          }}>
                            <div className="flex items-center gap-2">
                              <AlertCircle size={14} className="text-yellow-400" />
                              <p className="text-xs text-yellow-100">
                                This post was edited by a moderator
                                {post.editedByModeratorAt && ` on ${formatDate(post.editedByModeratorAt)}`}
                              </p>
                            </div>
                            {post.moderatorEditReason && (
                              <p className="text-xs text-yellow-100/80 mt-1">
                                Reason: {post.moderatorEditReason}
                              </p>
                            )}
                          </div>
                        )}

                        {!isForumPost && (
                          <>
                            {/* Media above content */}
                            {post.aboveContent && post.mediaUrl && (
                              <div className="mb-3 rounded-lg overflow-hidden">
                                <img
                                  src={post.mediaUrl}
                                  alt="Post media"
                                  className="w-full h-auto cursor-context-menu"
                                  style={{ maxHeight: '400px', objectFit: 'cover' }}
                                  onClick={(e) => e.preventDefault()}
                                  onContextMenu={(e) => e.stopPropagation()}
                                />
                              </div>
                            )}

                            <Link
                              to={
                                post.slug && community.nameSlug
                                  ? getCommunityPostUrl(community.nameSlug, post.slug)
                                  : `/posts/${post.id}`
                              }
                              state={{
                                from: 'community',
                                communityId: community.id,
                                themeContext: {
                                  type: 'community',
                                  communityId: community.id,
                                  colors: {
                                    primaryColor: localTheme.primaryColor,
                                    secondaryColor: localTheme.secondaryColor,
                                    textColor: localTheme.textColor,
                                    accentColor: localTheme.accentColor
                                  }
                                }
                              }}
                              className="block hover:opacity-80 transition-opacity cursor-pointer"
                            >
                              {post.title && (
                                <h2 className="text-xl font-bold mb-2" style={{ color: localTheme.textColor }}>{post.title}</h2>
                              )}
                              <p className="leading-relaxed" style={{ color: localTheme.textColor }}>{post.content}</p>
                            </Link>

                            {/* Media below content */}
                            {post.belowContent && post.mediaUrl && (
                              <div className="mt-3 rounded-lg overflow-hidden">
                                <img
                                  src={post.mediaUrl}
                                  alt="Post media"
                                  className="w-full h-auto cursor-context-menu"
                                  style={{ maxHeight: '400px', objectFit: 'cover' }}
                                  onClick={(e) => e.preventDefault()}
                                  onContextMenu={(e) => e.stopPropagation()}
                                />
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {/* Forum post content - outside the flex container to span full width */}
                    {isForumPost && (
                      <>
                        {/* Media above content */}
                        {post.aboveContent && post.mediaUrl && (
                          <div className="mb-3 rounded-lg overflow-hidden">
                            <img
                              src={post.mediaUrl}
                              alt="Post media"
                              className="w-full h-auto cursor-context-menu"
                              style={{ maxHeight: '400px', objectFit: 'cover' }}
                              onClick={(e) => e.preventDefault()}
                              onContextMenu={(e) => e.stopPropagation()}
                            />
                          </div>
                        )}

                        <Link
                          to={
                            post.slug && community.nameSlug
                              ? getForumPostUrl(community.nameSlug, post.slug)
                              : `/posts/${post.id}`
                          }
                          className="block hover:opacity-80 transition-opacity cursor-pointer"
                        >
                          <p className="leading-relaxed mb-3" style={{ color: localTheme.textColor }}>{post.content}</p>
                        </Link>

                        {/* Media below content */}
                        {post.belowContent && post.mediaUrl && (
                          <div className="mt-3 rounded-lg overflow-hidden">
                            <img
                              src={post.mediaUrl}
                              alt="Post media"
                              className="w-full h-auto cursor-context-menu"
                              style={{ maxHeight: '400px', objectFit: 'cover' }}
                              onClick={(e) => e.preventDefault()}
                              onContextMenu={(e) => e.stopPropagation()}
                            />
                          </div>
                        )}
                      </>
                    )}

                    <div className="flex items-center justify-between pt-4" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                      <div className="flex items-center gap-6">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleLike('post', post.id);
                          }}
                          className="flex items-center gap-2 transition-colors hover:text-red-400"
                          style={{ color: post.liked ? '#ef4444' : `${localTheme.textColor}99` }}
                        >
                          <Heart
                            size={18}
                            fill={post.liked ? '#ef4444' : 'none'}
                            color={post.liked ? '#ef4444' : undefined}
                          />
                          <span className="text-sm">{post.likes || 0}</span>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleDislike('post', post.id);
                          }}
                          className="flex items-center gap-2 transition-colors hover:text-orange-400"
                          style={{ color: post.disliked ? '#f97316' : `${localTheme.textColor}99` }}
                        >
                          <ThumbsDown
                            size={18}
                            fill={post.disliked ? '#f97316' : 'none'}
                            color={post.disliked ? '#f97316' : undefined}
                          />
                          <span className="text-sm">{post.dislikes || 0}</span>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            const url = post.postType === 'forum'
                              ? getForumPostUrl(community?.nameSlug || '', post.slug || '')
                              : getCommunityPostUrl(community?.nameSlug || '', post.slug || '');
                            navigate(url);
                          }}
                          className="flex items-center gap-2 transition-colors hover:text-purple-400"
                          style={{ color: `${localTheme.textColor}99` }}
                        >
                          <MessageSquare size={18} />
                          <span className="text-sm">{post._count?.replies || 0}</span>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleRepost(post.id);
                          }}
                          className="flex items-center gap-2 transition-colors hover:text-green-400"
                          style={{ color: `${localTheme.textColor}99` }}
                        >
                          <Repeat2 size={18} />
                          <span className="text-sm">{post._count?.reposts || 0}</span>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleShare(post.id);
                          }}
                          className="flex items-center gap-2 transition-colors hover:text-blue-400"
                          style={{ color: `${localTheme.textColor}99` }}
                        >
                          <Share2 size={18} />
                        </button>
                      </div>
                    </div>

                    {post.replies && post.replies.length > 0 && (() => {
                      // Map the replies to the expected Reply format
                      const mappedReplies = post.replies.map(r => ({
                        id: r.id,
                        content: r.content,
                        author: {
                          id: r.author.id,
                          username: r.author.username,
                          profileName: (r.author as any).profileName,
                          profilePicture: (r.author as any).profilePicture
                        },
                        likes: r.likes || 0,
                        dislikes: r.dislikes || 0,
                        createdAt: r.createdAt
                      }));
                      const previewComments = selectPreviewComments(mappedReplies as any);

                      return (
                        <div className="mt-4 pt-4" style={{ borderTop: `1px solid ${localTheme.accentColor}20` }}>
                          <div className="space-y-3">
                            {previewComments.map((reply) => (
                              <div key={reply.id} className="flex gap-3">
                                <Link
                                  to={`/user/${reply.author.username}`}
                                  className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                                  style={{
                                    background: `linear-gradient(to bottom right, ${localTheme.accentColor}, ${localTheme.accentColor}dd)`
                                  }}
                                >
                                  {reply.author.profilePicture ? (
                                    <img
                                      src={resolveAvatar(reply.author.profilePicture) || reply.author.profilePicture}
                                      alt={reply.author.username}
                                      className="w-full h-full rounded-full object-cover"
                                    />
                                  ) : (
                                    <PixelatedTurtle key={`turtle-reply-${localTheme.turtleColor}`} size={16} color={localTheme.turtleColor} />
                                  )}
                                </Link>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <Link
                                      to={`/user/${reply.author.username}`}
                                      className="text-xs font-semibold hover:opacity-80"
                                      style={{ color: localTheme.textColor }}
                                    >
                                      {reply.author.profileName || reply.author.username}
                                    </Link>
                                    <span className="text-xs" style={{ color: localTheme.textColor + '99' }}>
                                      {new Date(reply.createdAt).toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric'
                                      })}
                                    </span>
                                  </div>
                                  <p className="text-xs" style={{ color: localTheme.textColor }}>{reply.content}</p>
                                  <div className="flex items-center gap-3 mt-1">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        likeComment(reply.id, post.id);
                                      }}
                                      className="flex items-center gap-1 transition-colors hover:opacity-80"
                                      style={{ color: reply.liked ? '#ef4444' : localTheme.textColor + '99' }}
                                    >
                                      <Heart size={12} fill={reply.liked ? '#ef4444' : 'none'} />
                                      <span className="text-xs">{reply.likes}</span>
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        dislikeComment(reply.id, post.id);
                                      }}
                                      className="flex items-center gap-1 transition-colors hover:opacity-80"
                                      style={{ color: reply.disliked ? '#f97316' : localTheme.textColor + '99' }}
                                    >
                                      <ThumbsDown size={12} fill={reply.disliked ? '#f97316' : 'none'} />
                                      <span className="text-xs">{reply.dislikes}</span>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                          {(post._count?.replies || 0) > 3 && (
                            <div className="mt-2">
                              <button
                                onClick={() => {
                                  const url = post.postType === 'forum'
                                    ? getForumPostUrl(community?.nameSlug || '', post.slug || '')
                                    : getCommunityPostUrl(community?.nameSlug || '', post.slug || '');
                                  navigate(url);
                                }}
                                className="text-xs font-semibold hover:opacity-80 transition-opacity"
                                style={{ color: localTheme.accentColor }}
                              >
                                View all {post._count?.replies || 0} comments
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                  );
                });
                })()}
              </>
            )}

            {/* Forum Tab */}
            {activeTab === 'forum' && (
              <>
                {(() => {
                  // Use forum posts from state (initialized from tabContent, updated by event bus)
                  const forumPostsData = forumPosts || [];

                  // Apply sorting (backend provides default, but allow client-side override)
                  let sortedPosts = [...forumPostsData];
                  switch(sortBy) {
                    case 'newest':
                      sortedPosts = sortedPosts.sort((a, b) =>
                        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
                      );
                      break;
                    case 'oldest':
                      sortedPosts = sortedPosts.sort((a, b) =>
                        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
                      );
                      break;
                    case 'popular':
                      sortedPosts = sortedPosts.sort((a, b) => (b.likes || 0) - (a.likes || 0));
                      break;
                    default:
                      break;
                  }

                  return sortedPosts.map((forumPost, index) => {
                  const postStyle = getPostStyle(index);
                  return (
                  <div
                    key={forumPost.id}
                    ref={(el) => {
                      if (el) postRefs.current.set(forumPost.id, el);
                      else postRefs.current.delete(forumPost.id);
                    }}
                    data-post-id={forumPost.id}
                    data-index={index}
                    className="rounded-2xl backdrop-blur-sm border"
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                      marginBottom: isMobile ? `${scaleH(16, scale.h)}px` : '2rem',
                      padding: isMobile ? `${scaleH(16, scale.h)}px ${scaleW(12, scale.w)}px` : '1.5rem',
                      ...postStyle,
                    }}
                  >
                    {/* Post Badges */}
                    <PostBadges post={forumPost as any} textColor={localTheme.textColor} accentColor={localTheme.accentColor} />

                    {/* Moderate Dropdown */}
                    {currentUser && canModeratePost(forumPost as any, {
                      id: parseInt(currentUser.id),
                      username: currentUser.username,
                      isAdmin: (currentUser as any).isSiteAdmin || (currentUser as any).isSuperAdmin || false,
                      isModerator: (currentUser as any).isSiteModerator || false
                    }) && (
                    <div className="absolute top-3 right-3" style={{ zIndex: 10 }}>
                      <button
                        onClick={() => setShowModerateMenu(showModerateMenu === forumPost.id ? null : forumPost.id)}
                        className="p-1.5 rounded-lg transition-all hover:opacity-80"
                        style={{
                          backgroundColor: localTheme.accentColor + '20',
                          color: localTheme.textColor
                        }}
                      >
                        <MoreVertical size={16} />
                      </button>

                      {showModerateMenu === forumPost.id && (
                        <>
                          <div
                            className="absolute top-full right-0 mt-2 rounded-lg overflow-hidden shadow-2xl min-w-[140px]"
                            style={{
                              backgroundColor: localTheme.tabBarBg || 'rgba(30, 58, 95, 0.95)',
                              borderColor: localTheme.accentColor + '60',
                              border: '1px solid',
                              zIndex: 1001
                            }}
                          >
                            <button
                              onClick={() => handleEditPost(forumPost)}
                              className="flex items-center gap-2 w-full px-4 py-3 text-left text-sm font-medium transition-all"
                              style={{
                                color: localTheme.textColor,
                                backgroundColor: 'transparent'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = localTheme.accentColor + '20';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }}
                            >
                              <Edit3 size={16} />
                              Edit
                            </button>
                            <button
                              onClick={() => handlePinPost(forumPost)}
                              className="flex items-center gap-2 w-full px-4 py-3 text-left text-sm font-medium transition-all"
                              style={{
                                color: localTheme.textColor,
                                backgroundColor: 'transparent'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = localTheme.accentColor + '20';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }}
                            >
                              <Pin size={16} />
                              {forumPost.pinned ? 'Unpin' : 'Pin'}
                            </button>
                            <button
                              onClick={() => handleDeletePost(forumPost)}
                              className="flex items-center gap-2 w-full px-4 py-3 text-left text-sm font-medium transition-all"
                              style={{
                                color: '#ef4444',
                                backgroundColor: 'transparent'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#ef444420';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }}
                            >
                              <Trash size={16} />
                              Delete
                            </button>
                          </div>
                          <div
                            className="fixed inset-0"
                            style={{ zIndex: 90 }}
                            onClick={handleCloseShowModerateMenu}
                          />
                        </>
                      )}
                    </div>
                    )}

                    <div className="flex items-start gap-4 mb-4">
                      <div
                        className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/${forumPost.author.username}`);
                        }}
                        role="button"
                        tabIndex={0}
                      >
                        {forumPost.author.avatar ? (
                          <img src={resolveAvatar(forumPost.author.avatar) || forumPost.author.avatar} alt={forumPost.author.displayName} className="w-full h-full rounded-full object-cover" />
                        ) : (
                          <PixelatedTurtle key={`turtle-${localTheme.turtleColor}`} size={24} color={localTheme.turtleColor} />
                        )}
                      </div>
                      <div className="flex-1">
                        <Link
                          to={
                            forumPost.slug && community.nameSlug
                              ? getForumPostUrl(community.nameSlug, forumPost.slug)
                              : `/posts/${forumPost.id}`
                          }
                          state={{
                            from: 'community',
                            communityId: community.id,
                            themeContext: {
                              type: 'community',
                              communityId: community.id,
                              colors: {
                                primaryColor: localTheme.primaryColor,
                                secondaryColor: localTheme.secondaryColor,
                                textColor: localTheme.textColor,
                                accentColor: localTheme.accentColor
                              }
                            }
                          }}
                          className="flex items-center gap-2 mb-2 hover:opacity-80 transition-opacity cursor-pointer"
                          onClick={(e: React.MouseEvent) => e.stopPropagation()}
                        >
                          <h2 className="text-xl font-bold" style={{ color: localTheme.textColor }}>{forumPost.title}</h2>
                          {forumPost.pinned && <Pin size={16} style={{ color: localTheme.accentColor }} />}
                        </Link>
                        <div className="flex flex-col gap-0.5 mb-2">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold" style={{ color: localTheme.textColor }}>{forumPost.author.displayName}</h3>
                            <span className="text-sm" style={{ color: `${localTheme.textColor}99` }}>{formatTimeAgo(forumPost.createdAt)}</span>
                          </div>
                          {forumPost.author.username && (
                            <span className="text-xs" style={{ color: `${localTheme.textColor}99` }}>@{forumPost.author.username}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Media above content */}
                    {forumPost.aboveContent && (forumPost.mediaUrl || forumPost.imageUrl) && (
                      <div className="mb-3 rounded-lg overflow-hidden">
                        <img
                          src={forumPost.mediaUrl || forumPost.imageUrl}
                          alt="Forum post media"
                          className="w-full h-auto cursor-context-menu"
                          style={{ maxHeight: '400px', objectFit: 'cover' }}
                          onClick={(e) => e.preventDefault()}
                          onContextMenu={(e) => e.stopPropagation()}
                        />
                      </div>
                    )}

                    <Link
                      to={
                        forumPost.slug && community.nameSlug
                          ? getForumPostUrl(community.nameSlug, forumPost.slug)
                          : `/posts/${forumPost.id}`
                      }
                      state={{
                        from: 'community',
                        communityId: community.id,
                        themeContext: {
                          type: 'community',
                          communityId: community.id,
                          colors: {
                            primaryColor: localTheme.primaryColor,
                            secondaryColor: localTheme.secondaryColor,
                            textColor: localTheme.textColor,
                            accentColor: localTheme.accentColor
                          }
                        }
                      }}
                      className="block hover:opacity-80 transition-opacity cursor-pointer"
                    >
                      <p className="leading-relaxed mb-3" style={{ color: localTheme.textColor }}>{forumPost.content}</p>
                    </Link>

                    {/* Media below content */}
                    {forumPost.belowContent && (forumPost.mediaUrl || forumPost.imageUrl) && (
                      <div className="mt-3 rounded-lg overflow-hidden">
                        <img
                          src={forumPost.mediaUrl || forumPost.imageUrl}
                          alt="Forum post media"
                          className="w-full h-auto cursor-context-menu"
                          style={{ maxHeight: '400px', objectFit: 'cover' }}
                          onClick={(e) => e.preventDefault()}
                          onContextMenu={(e) => e.stopPropagation()}
                        />
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-4" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                      <div className="flex items-center gap-6">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleLike('post', forumPost.id);
                          }}
                          className="flex items-center gap-2 transition-colors hover:text-red-400"
                          style={{ color: `${localTheme.textColor}99` }}
                        >
                          <Heart
                            size={18}
                            fill={forumPost.liked ? '#ef4444' : 'none'}
                            color={forumPost.liked ? '#ef4444' : undefined}
                          />
                          <span className="text-sm">{forumPost.likes || 0}</span>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleDislike('post', forumPost.id);
                          }}
                          className="flex items-center gap-2 transition-colors hover:text-orange-400"
                          style={{ color: `${localTheme.textColor}99` }}
                        >
                          <ThumbsDown
                            size={18}
                            fill={forumPost.disliked ? '#f97316' : 'none'}
                            color={forumPost.disliked ? '#f97316' : undefined}
                          />
                          <span className="text-sm">{forumPost.dislikes || 0}</span>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            const url = getForumPostUrl(community?.nameSlug || '', forumPost.slug || '');
                            navigate(url);
                          }}
                          className="flex items-center gap-2 transition-colors hover:text-purple-400"
                          style={{ color: `${localTheme.textColor}99` }}
                        >
                          <MessageSquare size={18} />
                          <span className="text-sm">{forumPost._count?.replies || 0}</span>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleRepost(forumPost.id);
                          }}
                          className="flex items-center gap-2 transition-colors hover:text-green-400"
                          style={{ color: `${localTheme.textColor}99` }}
                        >
                          <Repeat2 size={18} />
                          <span className="text-sm">{forumPost._count?.reposts || 0}</span>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleShare(forumPost.id);
                          }}
                          className="flex items-center gap-2 transition-colors hover:text-blue-400"
                          style={{ color: `${localTheme.textColor}99` }}
                        >
                          <Share2 size={18} />
                        </button>
                      </div>
                    </div>

                    {forumPost.replies && forumPost.replies.length > 0 && (() => {
                      // Map the replies to the expected Reply format
                      const mappedReplies = forumPost.replies.map(r => ({
                        id: r.id,
                        content: r.content,
                        author: {
                          id: r.author.id,
                          username: r.author.username,
                          profileName: (r.author as any).profileName,
                          profilePicture: (r.author as any).profilePicture
                        },
                        likes: r.likes || 0,
                        dislikes: r.dislikes || 0,
                        createdAt: r.createdAt
                      }));
                      const previewComments = selectPreviewComments(mappedReplies as any);

                      return (
                        <div className="mt-4 pt-4" style={{ borderTop: `1px solid ${localTheme.accentColor}20` }}>
                          <div className="space-y-3">
                            {previewComments.map((reply) => (
                              <div key={reply.id} className="flex gap-3">
                                <Link
                                  to={`/user/${reply.author.username}`}
                                  className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                                  style={{
                                    background: `linear-gradient(to bottom right, ${localTheme.accentColor}, ${localTheme.accentColor}dd)`
                                  }}
                                >
                                  {reply.author.profilePicture ? (
                                    <img
                                      src={resolveAvatar(reply.author.profilePicture) || reply.author.profilePicture}
                                      alt={reply.author.username}
                                      className="w-full h-full rounded-full object-cover"
                                    />
                                  ) : (
                                    <PixelatedTurtle key={`turtle-reply-${localTheme.turtleColor}`} size={16} color={localTheme.turtleColor} />
                                  )}
                                </Link>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <Link
                                      to={`/user/${reply.author.username}`}
                                      className="text-xs font-semibold hover:opacity-80"
                                      style={{ color: localTheme.textColor }}
                                    >
                                      {reply.author.profileName || reply.author.username}
                                    </Link>
                                    <span className="text-xs" style={{ color: localTheme.textColor + '99' }}>
                                      {new Date(reply.createdAt).toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric'
                                      })}
                                    </span>
                                  </div>
                                  <p className="text-xs" style={{ color: localTheme.textColor }}>{reply.content}</p>
                                  <div className="flex items-center gap-3 mt-1">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        likeComment(reply.id, forumPost.id);
                                      }}
                                      className="flex items-center gap-1 transition-colors hover:opacity-80"
                                      style={{ color: reply.liked ? '#ef4444' : localTheme.textColor + '99' }}
                                    >
                                      <Heart size={12} fill={reply.liked ? '#ef4444' : 'none'} />
                                      <span className="text-xs">{reply.likes}</span>
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        dislikeComment(reply.id, forumPost.id);
                                      }}
                                      className="flex items-center gap-1 transition-colors hover:opacity-80"
                                      style={{ color: reply.disliked ? '#f97316' : localTheme.textColor + '99' }}
                                    >
                                      <ThumbsDown size={12} fill={reply.disliked ? '#f97316' : 'none'} />
                                      <span className="text-xs">{reply.dislikes}</span>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                          {(forumPost._count?.replies || 0) > 3 && (
                            <div className="mt-2">
                              <button
                                onClick={() => {
                                  const url = getForumPostUrl(community?.nameSlug || '', forumPost.slug || '');
                                  navigate(url);
                                }}
                                className="text-xs font-semibold hover:opacity-80 transition-opacity"
                                style={{ color: localTheme.accentColor }}
                              >
                                View all {forumPost._count?.replies || 0} comments
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                  );
                  });
                })()}
              </>
            )}

            {/* Chat Tab */}
            {activeTab === 'chat' && (
              <div className="rounded-2xl backdrop-blur-sm border" style={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderColor: 'rgba(255, 255, 255, 0.2)',
                width: isMobile ? '100%' : '50vw',
                maxWidth: isMobile ? '100%' : '50vw',
                height: isMobile ? `${scaleH(500, scale.h)}px` : '600px',
                marginBottom: isMobile ? `${scaleH(16, scale.h)}px` : '2rem',
                display: 'flex',
                flexDirection: 'column'
              }}>
                {/* Messages container with scrolling - limit to last 100 messages */}
                <div className="overflow-y-auto space-y-3" style={{
                  flex: '1 1 auto',
                  display: 'flex',
                  flexDirection: 'column',
                  padding: isMobile ? `${scaleH(12, scale.h)}px ${scaleW(12, scale.w)}px` : '1rem'
                }}>
                  {chatMessages?.slice(-100).map((message) => {
                    const frameColor = message.author.profileCustomization?.profileFrameColor || localTheme.accentColor;
                    const frameShape = message.author.profileCustomization?.profileFrameShape || 'circle';
                    const frameDesign = message.author.profileCustomization?.profileFrameDesign || 'coral';
                    const profileDecoration = message.author.profileDecoration;

                    return (
                    <div key={message.id} className="flex" style={{
                      gap: isMobile ? `${scaleW(8, scale.w)}px` : '0.75rem'
                    }}>
                      <Link
                        to={`/user/${message.author.username}`}
                        className="relative flex-shrink-0"
                        style={{
                          width: isMobile ? `${scaleW(32, scale.w)}px` : '2rem',
                          height: isMobile ? `${scaleW(32, scale.w)}px` : '2rem',
                          marginLeft: isMobile ? `${scaleW(4, scale.w)}px` : '0.3rem',
                          marginTop: isMobile ? `${scaleH(2, scale.h)}px` : '0.2rem'
                        }}
                        onContextMenu={(e: React.MouseEvent) => {
                          e.preventDefault();
                          window.open(`/user/${message.author.username}`, '_blank');
                        }}
                      >
                        <div
                          className={`w-full h-full border-2 overflow-hidden pixelated ${frameShape === 'circle' ? 'rounded-full' : 'rounded-lg'}`}
                          style={{ borderColor: frameColor }}
                        >
                          {message.author.avatar ? (
                            <img src={resolveAvatar(message.author.avatar) || message.author.avatar} alt={message.author.displayName} className="w-full h-full object-cover pixelated" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: localTheme.cardBg }}>
                              <PixelatedTurtle key={`turtle-chat-${localTheme.turtleColor}`} size={16} color={localTheme.turtleColor} />
                            </div>
                          )}
                        </div>
                      </Link>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-sm" style={{ color: localTheme.textColor }}>{message.author.displayName}</span>
                          <span className="text-xs" style={{ color: `${localTheme.textColor}99` }}>{formatTimeAgo(message.createdAt)}</span>
                          {!message.deletedAt && (canModerate || message.author.id === currentUser?.id) && (
                            <button
                              onClick={() => handleDeleteMessage(message.id)}
                              className="ml-auto text-red-400/60 hover:text-red-400 transition-colors"
                              title="Delete message"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                        {message.deletedAt ? (
                          <div className="text-sm italic bg-red-900/20 px-2 py-1 rounded border border-red-500/30" style={{ color: `${localTheme.textColor}66` }}>
                            <span style={{ paddingLeft: '0.3rem' }}>
                              {message.deletedBy && message.author.id === String(message.deletedBy)
                                ? 'Message deleted by user'
                                : 'Message deleted by moderator'}
                            </span>
                          </div>
                        ) : (
                          <p className="text-sm" style={{ color: `${localTheme.textColor}e6` }}>{message.content}</p>
                        )}
                      </div>
                    </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>

                {/* Chat input box at bottom */}
                <div className="border-t" style={{
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                  backgroundColor: 'rgba(0, 0, 0, 0.2)',
                  padding: isMobile ? `${scaleH(12, scale.h)}px ${scaleW(10, scale.w)}px` : '1rem 0.75rem',
                  flex: '0 0 auto'
                }}>
                  <div className="flex items-center" style={{
                    gap: isMobile ? `${scaleW(6, scale.w)}px` : '0.375rem'
                  }}>
                    <input
                      type="text"
                      value={chatMessage}
                      onChange={(e) => setChatMessage(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendChatMessage();
                        }
                      }}
                      placeholder="Type a message..."
                      disabled={isSendingMessage}
                      className="flex-1 rounded-lg outline-none transition-all"
                      style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        color: localTheme.textColor,
                        border: `1px solid ${localTheme.accentColor}40`,
                        paddingTop: isMobile ? `${scaleH(8, scale.h)}px` : '0.5rem',
                        paddingBottom: isMobile ? `${scaleH(8, scale.h)}px` : '0.5rem',
                        paddingLeft: isMobile ? `${scaleW(12, scale.w)}px` : '1.25rem',
                        paddingRight: isMobile ? `${scaleW(10, scale.w)}px` : '1rem',
                        fontSize: isMobile ? `${scaleW(14, scale.w)}px` : '1rem'
                      }}
                    />
                    <button
                      onClick={handleSendChatMessage}
                      disabled={!chatMessage.trim() || isSendingMessage}
                      className="rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-80"
                      style={{
                        backgroundColor: localTheme.accentColor,
                        color: localTheme.textColor,
                        padding: isMobile ? `${scaleH(8, scale.h)}px` : '0.5rem',
                        marginRight: isMobile ? `${scaleW(2, scale.w)}px` : '0.25rem'
                      }}
                    >
                      <Send size={isMobile ? scaleW(18, scale.w) : 20} />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

      {/* Share/Post Button - Position based on card alignment */}
      {sharePostButton}

      {/* Post Creation Modal */}
      {postCreationModal}
        </div>
      </div>

      {/* Standardized Theme Customizer Button */}
      <ThemeCustomizerButton
        onClick={handleToggleShowCustomizer}
        tooltipText="Customize Community Theme"
        accentColor={localTheme.accentColor}
        textColor={localTheme.textColor}
        show={isAdmin}
      />

      {/* Moderation Button - For site moderators/admins */}
      {currentUser && ((currentUser as any).isSiteModerator || (currentUser as any).isSiteAdmin || (currentUser as any).isSuperAdmin) && (
        <button
          onClick={handleToggleShowModerationModal}
          className="fixed rounded-lg shadow-lg font-semibold overflow-hidden flex items-center gap-2"
          style={{
            backgroundColor: localTheme.accentColor,
            color: localTheme.textColor,
            zIndex: 1000,
            padding: '0.75rem 2rem',
            fontSize: '1.125rem',
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            transform: showModerationModal ? 'scale(1.05)' : 'scale(1)',
            ...(communityCustomization.cardPosition === 'left'
              ? { right: '1.5rem', top: '14rem' }
              : { left: '1.5rem', top: '10rem' })
          }}
        >
          <Shield size={20} />
          <span>Moderate</span>
        </button>
      )}

      {/* Theme Customizer Modal */}
      {isAdmin && (
        <CommunityThemeCustomizer
          isOpen={showCustomizer}
          onClose={() => setShowCustomizer(false)}
          communityId={community.id}
          isOwnerOrAdmin={isAdmin}
          initialCustomization={customization}
          initialAnimations={animations}
          initialAnimationsEnabled={animationsEnabled}
        />
      )}

      {/* Repost Modal */}
      {showRepostModal && (
        <div
          ref={repostModalRef}
          className="fixed z-[9999] max-w-md w-full mx-4"
          style={{
            left: '50%',
            top: '5rem',
            transform: `translate(calc(-50% + ${repostModalPosition.x}px), ${repostModalPosition.y}px)`,
            cursor: isDraggingRepost ? 'grabbing' : 'default'
          }}
          onMouseMove={(e) => {
            if (isDraggingRepost) {
              const deltaX = e.clientX - dragStartRepost.x;
              const deltaY = e.clientY - dragStartRepost.y;
              setRepostModalPosition({ x: repostModalPosition.x + deltaX, y: repostModalPosition.y + deltaY });
              setDragStartRepost({ x: e.clientX, y: e.clientY });
            }
          }}
          onMouseUp={() => setIsDraggingRepost(false)}
          onMouseLeave={() => setIsDraggingRepost(false)}
        >
          <div className="rounded-lg backdrop-blur-sm border shadow-2xl" style={{
            background: localTheme.cardBg.replace(/[\d.]+\)$/, '0.95)'),
            borderColor: localTheme.accentColor + '40'
          }}>
            <div
              className="flex items-center justify-between mb-4 p-6 pb-2 cursor-grab active:cursor-grabbing"
              onMouseDown={(e) => {
                setIsDraggingRepost(true);
                setDragStartRepost({ x: e.clientX, y: e.clientY });
              }}
              style={{
                borderBottom: `1px solid ${localTheme.accentColor}20`,
                userSelect: 'none'
              }}
            >
              <h3 className="text-xl font-bold" style={{ color: localTheme.textColor }}>Repost</h3>
              <button
                onClick={() => {
                  setShowRepostModal(false);
                  setRepostModalPosition({ x: 0, y: 0 });
                }}
                className="transition-colors hover:opacity-70"
                style={{ color: localTheme.textColor }}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <X size={24} />
              </button>
            </div>
            <div className="px-6 pb-6">
            <p className="mb-4" style={{ color: localTheme.textColor + 'cc' }}>Add an optional comment to your repost:</p>
            <textarea
              value={repostComment}
              onChange={(e) => setRepostComment(e.target.value)}
              placeholder="What do you think about this?"
              className="w-full p-3 rounded-lg outline-none resize-none transition-all"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: localTheme.textColor,
                border: `1px solid ${localTheme.accentColor}40`
              }}
              rows={4}
              maxLength={280}
            />
            <div className="text-right text-sm mb-4" style={{ color: localTheme.textColor + '99' }}>{repostComment.length}/280</div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowRepostModal(false);
                  setRepostComment('');
                }}
                className="flex-1 py-2 rounded-lg transition-all"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  color: localTheme.textColor,
                  border: `1px solid ${localTheme.accentColor}40`
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitRepost}
                disabled={isReposting}
                className="flex-1 py-2 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: localTheme.accentColor,
                  color: localTheme.textColor
                }}
              >
                {isReposting ? 'Reposting...' : 'Repost'}
              </button>
            </div>
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-[100] max-w-md w-full mx-4">
          <div className="rounded-lg p-6 backdrop-blur-sm border shadow-2xl" style={{
            background: localTheme.cardBg.replace(/[\d.]+\)$/, '0.95)'),
            borderColor: localTheme.accentColor + '40'
          }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold" style={{ color: localTheme.textColor }}>Share Post</h3>
              <button onClick={handleCloseShowShareModal} className="transition-colors hover:opacity-70" style={{ color: localTheme.textColor }}>
                <X size={24} />
              </button>
            </div>
            <p className="mb-4" style={{ color: localTheme.textColor + 'cc' }}>Share this post:</p>
            <button
              onClick={handleCopyLink}
              className="w-full py-3 rounded-lg transition-all flex items-center justify-center gap-2"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: localTheme.textColor,
                border: `1px solid ${localTheme.accentColor}40`
              }}
            >
              <Link2 size={18} />
              Copy Link
            </button>
          </div>
        </div>
      )}

      {/* Edit Post Modal */}
      {editingPost && (
        <EditPostModal
          post={editingPost}
          isOpen={!!editingPost}
          onClose={() => setEditingPost(null)}
          onSave={handleSaveEdit}
        />
      )}

      {/* Moderation Modal */}
      {showModerationModal && currentUser && ((currentUser as any).isSiteModerator || (currentUser as any).isSiteAdmin || (currentUser as any).isSuperAdmin) && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
          onClick={handleCloseShowModerationModal}
        >
          <div
            className="rounded-lg backdrop-blur-sm border p-6 w-[600px] max-h-[80vh] overflow-y-auto"
            style={{
              background: theme.cardBg?.replace(/[\d.]+\)$/, '0.95)') || 'rgba(255, 255, 255, 0.95)',
              borderColor: theme.accentColor + '40'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Shield size={24} style={{ color: theme.accentColor }} />
                <h2 className="text-2xl font-bold" style={{ color: theme.textColor }}>
                  Moderate Community
                </h2>
              </div>
              <button
                onClick={handleCloseShowModerationModal}
                className="p-2 hover:bg-white/10 rounded transition-colors"
                style={{ color: theme.textColor + '99' }}
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              {/* Moderator Features */}
              <div className="space-y-4 pb-4" style={{ borderBottom: `1px solid ${theme.accentColor}20` }}>
                <p className="text-sm font-semibold" style={{ color: theme.accentColor }}>
                  Moderator Features
                </p>

                {/* Community Name */}
                <div>
                  <label className="block text-sm mb-2" style={{ color: theme.textColor + '99' }}>
                    Community Name
                  </label>
                  <input
                    type="text"
                    value={modCommunityName}
                    onChange={(e) => setModCommunityName(e.target.value)}
                    className="w-full px-4 py-2 rounded-lg outline-none"
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      color: theme.textColor,
                      border: `1px solid ${theme.accentColor}40`
                    }}
                  />
                </div>

                {/* Community Title */}
                <div>
                  <label className="block text-sm mb-2" style={{ color: theme.textColor + '99' }}>
                    Community Title
                  </label>
                  <input
                    type="text"
                    value={modCommunityTitle}
                    onChange={(e) => setModCommunityTitle(e.target.value)}
                    className="w-full px-4 py-2 rounded-lg outline-none"
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      color: theme.textColor,
                      border: `1px solid ${theme.accentColor}40`
                    }}
                  />
                </div>

                {/* Bio/Description */}
                <div>
                  <label className="block text-sm mb-2" style={{ color: theme.textColor + '99' }}>
                    Bio
                  </label>
                  <textarea
                    value={modCommunityBio}
                    onChange={(e) => setModCommunityBio(e.target.value)}
                    className="w-full px-4 py-2 rounded-lg outline-none resize-none"
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      color: theme.textColor,
                      border: `1px solid ${theme.accentColor}40`,
                      minHeight: '100px'
                    }}
                  />
                </div>
              </div>

              {/* Admin Features */}
              {(currentUser as any).isAdmin && (
                <div className="space-y-4 pt-4">
                  <p className="text-sm font-semibold" style={{ color: '#ef4444' }}>
                    Admin Features
                  </p>

                  <p className="text-xs" style={{ color: theme.textColor + '80' }}>
                    Additional admin-only features can be added here.
                  </p>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowModerationModal(false);
                  // Reset values
                  setModCommunityName(community.name);
                  setModCommunityTitle('Earth Servers');
                  setModCommunityBio(community.description || '');
                }}
                className="flex-1 px-4 py-3 rounded-lg font-semibold transition-all hover:opacity-80"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  color: theme.textColor,
                  border: `1px solid ${theme.accentColor}40`
                }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  try {
                    const updates: any = {
                      name: modCommunityName,
                      description: modCommunityBio,
                    };

                    const response = await fetchWithAuth(`/api/moderation/communities/${community.id}`, {
                      method: 'PUT',
                      headers: {
                        'Content-Type': 'application/json',
                      },
                      credentials: 'include',
                      body: JSON.stringify(updates),
                    });

                    if (!response.ok) {
                      const error = await response.json();
                      throw new Error(error.error || 'Failed to update community');
                    }

                    const data = await response.json();
                    alert('Community moderation changes saved successfully');
                    setShowModerationModal(false);
                    window.location.reload();
                  } catch (error) {
                    alert(error instanceof Error ? error.message : 'Failed to save changes');
                  }
                }}
                className="flex-1 px-4 py-3 rounded-lg font-semibold transition-all hover:opacity-90"
                style={{
                  backgroundColor: theme.accentColor,
                  color: theme.textColor
                }}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
